import { PLAElementRecord, PLARelationRecord, PLAProject, AttributeValue, PLAElementTypeDefinition, PLAValueType, isPLANumberValueType, PLA_CUBE_TOTAL_KEY, PLA_CUBE_KEY_SEPARATOR, getPLACubeCellKey } from "./PLAState";
import chroma from "chroma-js";

type TokenType = "number" | "string" | "identifier" | "attribute" | "operator" | "paren" | "comma" | "dot" | "comparison" | "question" | "colon" | "pipe" | "cubejson";
type Token = { type: TokenType; value: string };
type StringExpressionNode = { type: "constant"; value: string } | { type: "attribute"; name: string } | { type: "concat"; left: StringExpressionNode; right: StringExpressionNode };
type AttributeReference = { type: "literal"; name: string } | { type: "localAttribute"; name: string } | { type: "stringExpression"; expression: StringExpressionNode };

type ExpressionNode =
   | { type: "self" }
   | { type: "pipe"; input: ExpressionNode; name: string; args: ExpressionNode[] }
   | { type: "number"; value: number }
   | { type: "string"; value: string }
   | { type: "cubeLiteral"; dimensions: string[]; cells: { members: string[]; value: number }[] }
   | { type: "attribute"; name: string }
   | { type: "binary"; operator: string; left: ExpressionNode; right: ExpressionNode }
   | { type: "conditional"; condition: ExpressionNode; whenTrue: ExpressionNode; whenFalse: ExpressionNode }
   | { type: "function"; name: string; args: ExpressionNode[] }
   | { type: "traversal"; aggregator: string; relationType: string; attribute: AttributeReference; deep?: boolean; functionName?: string }
   | { type: "filteredTraversal"; aggregator: string; relationType: string; attribute: AttributeReference; filterAttribute: AttributeReference; filterValue: ExpressionNode; functionName: string }
   | { type: "parentLookup"; relationType: string; attribute: AttributeReference }
   | { type: "parentTraversal"; aggregator: string; parentRelationType: string; relationType: string; attribute: AttributeReference };

export type PLAEvaluationContext = {
   element: PLAElementRecord;
   elements: PLAElementRecord[];
   relations: PLARelationRecord[];
   project?: PLAProject;
   calculated?: Record<string, any>;
   resolveCalculatedAttribute?: (element: PLAElementRecord, attributeId: string) => any;
};

export type PLAExpressionValidationContext = {
   project?: PLAProject;
   elementTypeId?: string;
   elementType?: PLAElementTypeDefinition;
};

export type PLAExpressionExplanationStep = {
   label: string;
   value: any;
   valueType: string;
   details?: string[];
   children?: PLAExpressionExplanationStep[];
};

export type PLAExpressionExplanation = {
   expression: string;
   result: any;
   valueType: string;
   root: PLAExpressionExplanationStep;
   lines: string[];
};

const aggregateFunctions = ["sum", "avg", "min", "max", "count"];
const traversalFunctions = [...aggregateFunctions, "single"];
const filteredTraversalFunctions: Record<string, string> = {
   filterSum: "sum",
   filterAvg: "avg",
   filterMin: "min",
   filterMax: "max",
   filterCount: "count",
   filterSingle: "single"
};
const deepTraversalFunctions: Record<string, string> = {
   deepSum: "sum",
   deepAvg: "avg",
   deepMin: "min",
   deepMax: "max",
   deepCount: "count",
   deepSingle: "single"
};
const parentTraversalFunctions: Record<string, string> = {
   parentSum: "sum",
   parentAvg: "avg",
   parentMin: "min",
   parentMax: "max",
   parentCount: "count",
   parentSingle: "single"
};
const parentLookupFunction = "parent";
const parsedExpressionCache = new Map<string, ExpressionNode>();
const parsedExpressionCacheMaxSize = 500;
const ELEMENT_NAME_ATTRIBUTE = "name";

function isElementNameAttribute(reference: string): boolean {
   return reference.trim().toLowerCase() === ELEMENT_NAME_ATTRIBUTE;
}

type RelationIndexes = {
   outgoing: Map<string, PLARelationRecord[]>;
   incoming: Map<string, PLARelationRecord[]>;
   outgoingByType: Map<string, PLARelationRecord[]>;
   incomingByType: Map<string, PLARelationRecord[]>;
};

const elementIndexCache = new WeakMap<PLAElementRecord[], Map<string, PLAElementRecord>>();
const relationIndexCache = new WeakMap<PLARelationRecord[], RelationIndexes>();

function indexKey(elementId: string, relationType: string) {
   return `${elementId}\u0000${relationType}`;
}

function getElementsById(elements: PLAElementRecord[]): Map<string, PLAElementRecord> {
   const cached = elementIndexCache.get(elements);
   if (cached) return cached;
   const indexed = new Map(elements.map((element) => [element.id, element]));
   elementIndexCache.set(elements, indexed);
   return indexed;
}

const elementsByTypeCache = new WeakMap<PLAElementRecord[], Map<string, PLAElementRecord[]>>();

function getElementsByType(elements: PLAElementRecord[], elementType: string): PLAElementRecord[] {
   let indexed = elementsByTypeCache.get(elements);
   if (!indexed) {
      indexed = new Map();
      for (const element of elements) {
         const group = indexed.get(element.elementType);
         if (group) group.push(element);
         else indexed.set(element.elementType, [element]);
      }
      elementsByTypeCache.set(elements, indexed);
   }
   return indexed.get(elementType) || [];
}
function addIndexedRelation(map: Map<string, PLARelationRecord[]>, key: string, relation: PLARelationRecord) {
   const existing = map.get(key);
   if (existing) existing.push(relation);
   else map.set(key, [relation]);
}

function getRelationIndexes(relations: PLARelationRecord[]): RelationIndexes {
   const cached = relationIndexCache.get(relations);
   if (cached) return cached;
   const indexed: RelationIndexes = { outgoing: new Map(), incoming: new Map(), outgoingByType: new Map(), incomingByType: new Map() };
   relations.forEach((relation) => {
      addIndexedRelation(indexed.outgoing, relation.fromId, relation);
      addIndexedRelation(indexed.incoming, relation.toId, relation);
      addIndexedRelation(indexed.outgoingByType, indexKey(relation.fromId, relation.relationType), relation);
      addIndexedRelation(indexed.incomingByType, indexKey(relation.toId, relation.relationType), relation);
   });
   relationIndexCache.set(relations, indexed);
   return indexed;
}

function getRelatedRelations(context: PLAEvaluationContext, elementId: string, relationTypeId: string, direction: "outgoing" | "incoming"): PLARelationRecord[] {
   const indexes = getRelationIndexes(context.relations);
   if (relationTypeId === "outgoing") return indexes.outgoing.get(elementId) || [];
   if (relationTypeId === "incoming") return indexes.incoming.get(elementId) || [];
   return direction === "incoming" ? indexes.incomingByType.get(indexKey(elementId, relationTypeId)) || [] : indexes.outgoingByType.get(indexKey(elementId, relationTypeId)) || [];
}

function readEscapedText(expression: string, start: number, endChar: string): { value: string; end: number } {
   let value = "";
   let index = start;
   while (index < expression.length) {
      const char = expression[index];
      if (char === "\\") {
         if (index + 1 >= expression.length) throw new Error("Invalid escape sequence");
         value += expression[index + 1];
         index += 2;
         continue;
      }
      if (char === endChar) return { value, end: index };
      value += char;
      index++;
   }
   throw new Error(`Missing closing ${endChar}`);
}

function readQuotedIdentifier(expression: string, start: number): { value: string; end: number } {
   const quote = expression[start];
   const result = readEscapedText(expression, start + 1, quote);
   return { value: result.value, end: result.end + 1 };
}

function readBracketedAttributeReference(expression: string, start: number): { value: string; end: number } {
   let value = "";
   let depth = 0;
   let index = start;
   while (index < expression.length) {
      const char = expression[index];
      if (char === "\\") {
         if (index + 1 >= expression.length) throw new Error("Invalid escape sequence");
         value += expression[index + 1];
         index += 2;
         continue;
      }
      if (char === '"' || char === "'") {
         const quoted = readEscapedText(expression, index + 1, char);
         value += expression.slice(index, quoted.end + 1);
         index = quoted.end + 1;
         continue;
      }
      if (char === "[") {
         depth++;
         value += char;
         index++;
         continue;
      }
      if (char === "]") {
         if (depth === 0) return { value, end: index };
         depth--;
         value += char;
         index++;
         continue;
      }
      value += char;
      index++;
   }
   throw new Error("Missing closing ]");
}

function readIdentifier(expression: string, start: number): { value: string; end: number } | undefined {
   let value = "";
   let index = start;
   while (index < expression.length) {
      const char = expression[index];
      if (char === "\\") {
         if (index + 1 >= expression.length) throw new Error("Invalid escape sequence");
         value += expression[index + 1];
         index += 2;
         continue;
      }
      if (/[A-Za-z0-9_ÆØÅæøå-]/.test(char)) {
         value += char;
         index++;
         continue;
      }
      break;
   }
   if (!value || !/^[A-Za-z_ÆØÅæøå]/.test(value)) return undefined;
   return { value, end: index };
}

function findJsonObjectEnd(expression: string, start: number): number {
   let depth = 0;
   let inString = false;
   for (let index = start; index < expression.length; index++) {
      const char = expression[index];
      if (inString) {
         if (char === "\\") index++;
         else if (char === '"') inString = false;
      } else if (char === '"') inString = true;
      else if (char === "{") depth++;
      else if (char === "}" && --depth === 0) return index + 1;
   }
   throw new Error("Unterminated cube literal");
}

function parseCubeJson(text: string): Extract<ExpressionNode, { type: "cubeLiteral" }> {
   let data: any;
   try { data = JSON.parse(text); } catch { throw new Error("Invalid cube literal JSON"); }
   if (!data || !Array.isArray(data.dimensions) || !data.dimensions.length || !data.dimensions.every((name: unknown) => typeof name === "string" && name.trim())) throw new Error("Cube literal requires a 'dimensions' array of names");
   if (!Array.isArray(data.cells)) throw new Error("Cube literal requires a 'cells' array");
   const cells = data.cells.map((cell: any) => {
      if (!cell || !Array.isArray(cell.members) || cell.members.length !== data.dimensions.length || !cell.members.every((member: unknown) => typeof member === "string")) throw new Error("Each cube literal cell needs one member per dimension");
      if (typeof cell.value !== "number" || !Number.isFinite(cell.value)) throw new Error("Each cube literal cell needs a numeric 'value'");
      return { members: cell.members as string[], value: cell.value as number };
   });
   return { type: "cubeLiteral", dimensions: data.dimensions, cells };
}

function serializeCubeLiteral(node: Extract<ExpressionNode, { type: "cubeLiteral" }>, quote: (name: string) => string): string {
   const names = (values: string[]) => values.length === 1 ? quote(values[0]) : `(${values.map(quote).join(", ")})`;
   return `cube(${[names(node.dimensions), ...node.cells.map((cell) => `${names(cell.members)}: ${cell.value}`)].join(", ")})`;
}

export function tokenizePLAExpression(expression: string): Token[] {
   const tokens: Token[] = [];
   let index = 0;

   while (index < expression.length) {
      const char = expression[index];
      if (/\s/.test(char)) {
         index++;
         continue;
      }
      if (char === "[") {
         const result = readBracketedAttributeReference(expression, index + 1);
         const name = result.value.trim();
         if (!name) throw new Error("Empty attribute reference");
         tokens.push({ type: "attribute", value: name });
         index = result.end + 1;
         continue;
      }
      if (char === '"' || char === "'") {
         const identifier = readQuotedIdentifier(expression, index);
         tokens.push({ type: "string", value: identifier.value });
         index = identifier.end;
         continue;
      }
      if (char === "{") {
         const end = findJsonObjectEnd(expression, index);
         tokens.push({ type: "cubejson", value: expression.slice(index, end) });
         index = end;
         continue;
      }
      const comparison = expression.slice(index).match(/^(>=|<=|==|!=|>|<)/);
      if (comparison) {
         tokens.push({ type: "comparison", value: comparison[1] });
         index += comparison[1].length;
         continue;
      }
      if (/[+\-*/]/.test(char)) {
         tokens.push({ type: "operator", value: char });
         index++;
         continue;
      }
      if (char === "(" || char === ")") {
         tokens.push({ type: "paren", value: char });
         index++;
         continue;
      }
      if (char === ",") {
         tokens.push({ type: "comma", value: char });
         index++;
         continue;
      }
      if (char === "?") {
         tokens.push({ type: "question", value: char });
         index++;
         continue;
      }
      if (char === "|") {
         tokens.push({ type: "pipe", value: char });
         index++;
         continue;
      }
      if (char === ":") {
         tokens.push({ type: "colon", value: char });
         index++;
         continue;
      }
      if (char === ".") {
         tokens.push({ type: "dot", value: char });
         index++;
         continue;
      }
      const numberMatch = expression.slice(index).match(/^\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/);
      if (numberMatch) {
         tokens.push({ type: "number", value: numberMatch[0] });
         index += numberMatch[0].length;
         continue;
      }
      const identifier = readIdentifier(expression, index);
      if (identifier) {
         tokens.push({ type: "identifier", value: identifier.value });
         index = identifier.end;
         continue;
      }
      throw new Error(`Unexpected character '${char}'`);
   }

   return tokens;
}

class PLAExpressionParser {
   private position = 0;

   constructor(private readonly tokens: Token[]) { }

   parse(): ExpressionNode {
      const node = this.parseConditional();
      if (this.peek()) throw new Error(`Unexpected token '${this.peek()?.value}'`);
      return node;
   }

   private parseConditional(): ExpressionNode {
      const condition = this.parseComparison();
      if (this.peek()?.type !== "question") return condition;
      this.consume();
      const whenTrue = this.parseConditional();
      this.expect("colon", ":");
      const whenFalse = this.parseConditional();
      return { type: "conditional", condition, whenTrue, whenFalse };
   }

   private parseComparison(): ExpressionNode {
      let node = this.parseAdditive();
      while (this.peek()?.type === "comparison") {
         const operator = this.consume().value;
         node = { type: "binary", operator, left: node, right: this.parseAdditive() };
      }
      return node;
   }

   private parseAdditive(): ExpressionNode {
      let node = this.parseMultiplicative();
      while (this.peek()?.type === "operator" && ["+", "-"].includes(this.peek()!.value)) {
         const operator = this.consume().value;
         node = { type: "binary", operator, left: node, right: this.parseMultiplicative() };
      }
      return node;
   }

   private parseMultiplicative(): ExpressionNode {
      let node = this.parseUnary();
      while (this.peek()?.type === "operator" && ["*", "/"].includes(this.peek()!.value)) {
         const operator = this.consume().value;
         node = { type: "binary", operator, left: node, right: this.parseUnary() };
      }
      return node;
   }

   private parseUnary(): ExpressionNode {
      if (this.peek()?.type === "operator" && this.peek()?.value === "-") {
         this.consume();
         return { type: "binary", operator: "*", left: { type: "number", value: -1 }, right: this.parseUnary() };
      }
      let node = this.parsePrimary();
      while (this.peek()?.type === "pipe") {
         this.consume();
         const name = this.expect("identifier").value;
         this.expect("paren", "(");
         const args: ExpressionNode[] = [];
         if (this.peek()?.value !== ")") {
            do {
               args.push(this.parseConditional());
               if (this.peek()?.type !== "comma") break;
               this.consume();
            } while (this.peek() !== undefined);
         }
         this.expect("paren", ")");
         validatePipeArity(name, args.length);
         node = { type: "pipe", input: node, name, args };
      }
      return node;
   }

   private parsePrimary(): ExpressionNode {
      const token = this.peek();
      if (!token) throw new Error("Unexpected end of expression");
      if (token.type === "number") {
         this.consume();
         return { type: "number", value: Number(token.value) };
      }
      if (token.type === "string") {
         this.consume();
         return { type: "string", value: token.value };
      }
      if (token.type === "cubejson") {
         this.consume();
         return parseCubeJson(token.value);
      }
      if (token.type === "attribute") {
         this.consume();
         return { type: "attribute", name: token.value };
      }
      if (token.type === "paren" && token.value === "(") {
         this.consume();
         const node = this.parseConditional();
         this.expect("paren", ")");
         return node;
      }
      if (token.type === "identifier") {
         if (token.value === "self" && this.tokens[this.position + 1]?.value !== "(") {
            this.consume();
            return { type: "self" };
         }
         return this.parseFunctionOrTraversal();
      }
      throw new Error(`Unexpected token '${token.value}'`);
   }

   private parseFunctionOrTraversal(): ExpressionNode {
      const functionName = this.expect("identifier").value;
      this.expect("paren", "(");
      if (functionName === "cube") return this.parseCubeLiteral();
      const legacy = this.parseLegacyTraversal(functionName);
      if (legacy) return legacy;
      const args: ExpressionNode[] = [];
      if (!(this.peek()?.type === "paren" && this.peek()?.value === ")")) {
         let hasMoreArgs = true;
         while (hasMoreArgs) {
            args.push(this.parseConditional());
            hasMoreArgs = this.peek()?.type === "comma";
            if (hasMoreArgs) this.consume();
         }
      }
      this.expect("paren", ")");
      return { type: "function", name: functionName, args };
   }

   private parseLegacyTraversal(functionName: string): ExpressionNode | undefined {
      if (filteredTraversalFunctions[functionName] && this.isNameToken(this.peek()) && this.tokens[this.position + 1]?.type === "dot") {
         const relationType = this.expectName().value;
         this.expect("dot", ".");
         const attribute = this.parseAttributeReference();
         this.expect("comma", ",");
         const filterAttribute = this.parseAttributeReference();
         this.expect("comma", ",");
         const filterValue = this.parseConditional();
         this.expect("paren", ")");
         return { type: "filteredTraversal", aggregator: filteredTraversalFunctions[functionName], relationType, attribute, filterAttribute, filterValue, functionName };
      }
      if (parentTraversalFunctions[functionName] && this.isNameToken(this.peek()) && this.tokens[this.position + 1]?.type === "dot" && this.isNameToken(this.tokens[this.position + 2]) && this.tokens[this.position + 3]?.type === "dot") {
         const parentRelationType = this.expectName().value;
         this.expect("dot", ".");
         const relationType = this.expectName().value;
         this.expect("dot", ".");
         const attribute = this.parseAttributeReference();
         this.expect("paren", ")");
         return { type: "parentTraversal", aggregator: parentTraversalFunctions[functionName], parentRelationType, relationType, attribute };
      }
      if ((traversalFunctions.includes(functionName) || deepTraversalFunctions[functionName] || functionName === parentLookupFunction) && this.isNameToken(this.peek()) && this.tokens[this.position + 1]?.type === "dot") {
         const relationType = this.expectName().value;
         this.expect("dot", ".");
         const attribute = this.parseAttributeReference();
         this.expect("paren", ")");
         if (functionName === parentLookupFunction) return { type: "parentLookup", relationType, attribute };
         return { type: "traversal", aggregator: deepTraversalFunctions[functionName] || functionName, relationType, attribute, deep: Boolean(deepTraversalFunctions[functionName]), functionName };
      }
      return undefined;
   }

   private parseAttributeReference(): AttributeReference {
      const token = this.peek();
      if (token?.type === "identifier" || token?.type === "string") {
         const name = this.consume().value;
         if (!name.trim()) throw new Error("Empty quoted name");
         return { type: "literal", name };
      }
      if (token?.type === "attribute") return parseIndirectAttributeReference(this.consume().value);
      throw new Error("Expected attribute name");
   }

   private parseCubeNames(): string[] {
      if (this.peek()?.type === "paren" && this.peek()?.value === "(") {
         this.consume();
         const names = [this.expectName().value];
         while (this.peek()?.type === "comma") {
            this.consume();
            names.push(this.expectName().value);
         }
         this.expect("paren", ")");
         return names;
      }
      return [this.expectName().value];
   }

   // cube("Dim", "A": 1, "B": 2) or cube(("Dim1", "Dim2"), ("A", "X"): 1, ...)
   private parseCubeLiteral(): ExpressionNode {
      const dimensions = this.parseCubeNames();
      const cells: { members: string[]; value: number }[] = [];
      while (this.peek()?.type === "comma") {
         this.consume();
         const members = this.parseCubeNames();
         if (members.length !== dimensions.length) throw new Error(`Cube cell needs ${dimensions.length} member(s), one per dimension`);
         this.expect("colon", ":");
         const negative = this.peek()?.type === "operator" && this.peek()?.value === "-";
         if (negative) this.consume();
         const value = Number(this.expect("number").value);
         cells.push({ members, value: negative ? -value : value });
      }
      this.expect("paren", ")");
      return { type: "cubeLiteral", dimensions, cells };
   }

   private isNameToken(token: Token | undefined): boolean {
      return token?.type === "identifier" || token?.type === "string";
   }

   private expectName(): Token {
      const token = this.consume();
      if (!this.isNameToken(token)) throw new Error("Expected name");
      if (!token.value.trim()) throw new Error("Empty quoted name");
      return token;
   }

   private peek(): Token | undefined {
      return this.tokens[this.position];
   }

   private consume(): Token {
      return this.tokens[this.position++];
   }

   private expect(type: TokenType, value?: string): Token {
      const token = this.consume();
      if (!token || token.type !== type || (value !== undefined && token.value !== value)) {
         throw new Error(`Expected ${value ?? type}`);
      }
      return token;
   }
}

class PLAStringExpressionParser {
   private position = 0;

   constructor(private readonly expression: string) { }

   parse(): StringExpressionNode {
      const node = this.parseTerm();
      this.skipWhitespace();
      if (this.peek() !== "+") {
         if (this.position < this.expression.length) throw new Error(`Unexpected string expression token '${this.peek()}'`);
         return node;
      }
      this.consume();
      return { type: "concat", left: node, right: this.parse() };
   }

   private parseTerm(): StringExpressionNode {
      this.skipWhitespace();
      const char = this.peek();
      if (!char) throw new Error("Unexpected end of string expression");
      if (char === '"' || char === "'") {
         const quoted = readEscapedText(this.expression, this.position + 1, char);
         this.position = quoted.end + 1;
         return { type: "constant", value: quoted.value };
      }
      if (char === "[") {
         const reference = readEscapedText(this.expression, this.position + 1, "]");
         const name = reference.value.trim();
         if (!name) throw new Error("Empty attribute reference");
         this.position = reference.end + 1;
         return { type: "attribute", name };
      }

      const start = this.position;
      while (this.position < this.expression.length && this.expression[this.position] !== "+") this.position++;
      const name = this.expression.slice(start, this.position).trim();
      if (!name) throw new Error("Expected string expression term");
      return { type: "attribute", name };
   }

   private peek(): string | undefined {
      return this.expression[this.position];
   }

   private consume(): string {
      return this.expression[this.position++];
   }

   private skipWhitespace() {
      while (/\s/.test(this.peek() || "")) this.position++;
   }
}

function parseIndirectAttributeReference(expression: string): AttributeReference {
   if (!/[+"'\]]/.test(expression)) return { type: "localAttribute", name: expression };
   return { type: "stringExpression", expression: new PLAStringExpressionParser(expression).parse() };
}

export function parsePLAExpression(expression: string): ExpressionNode {
   const cached = parsedExpressionCache.get(expression);
   if (cached) return cached;
   const parsed = new PLAExpressionParser(tokenizePLAExpression(expression)).parse();
   const oldestExpression = parsedExpressionCache.keys().next().value;
   if (parsedExpressionCache.size >= parsedExpressionCacheMaxSize && oldestExpression !== undefined) parsedExpressionCache.delete(oldestExpression);
   parsedExpressionCache.set(expression, parsed);
   return parsed;
}

function quotedExpressionName(value: string): string {
   return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

function serializeAttributeName(value: string): string {
   return `[${value.replace(/[\\[\]"']/g, "\\$&")}]`;
}

function serializeStringExpression(node: StringExpressionNode): string {
   if (node.type === "constant") return quotedExpressionName(node.value);
   if (node.type === "attribute") return serializeAttributeName(node.name);
   return `concat(${serializeStringExpression(node.left)}, ${serializeStringExpression(node.right)})`;
}

function serializeAttributeReference(reference: AttributeReference): string {
   if (reference.type === "literal") return quotedExpressionName(reference.name);
   if (reference.type === "localAttribute") return serializeAttributeName(reference.name);
   if (reference.expression.type === "constant") return quotedExpressionName(reference.expression.value.trim());
   return serializeStringExpression(reference.expression);
}

function serializePipeExpression(node: ExpressionNode): string {
   switch (node.type) {
      case "self": return "self";
      case "number": return Number.isFinite(node.value) ? String(node.value) : "1e309";
      case "string": return quotedExpressionName(node.value);
      case "cubeLiteral": return serializeCubeLiteral(node, quotedExpressionName);
      case "attribute": return serializeAttributeName(node.name);
      case "binary": return `(${serializePipeExpression(node.left)} ${node.operator} ${serializePipeExpression(node.right)})`;
      case "conditional": return `(${serializePipeExpression(node.condition)} ? ${serializePipeExpression(node.whenTrue)} : ${serializePipeExpression(node.whenFalse)})`;
      case "function": return `${node.name}(${node.args.map(serializePipeExpression).join(", ")})`;
      case "pipe": return `(${serializePipeExpression(node.input)} | ${node.name}(${node.args.map(serializePipeExpression).join(", ")}))`;
      case "traversal": {
         const reference = serializeAttributeReference(node.attribute);
         return `(self | downUntil(${quotedExpressionName(node.relationType)}, ${reference}${node.deep ? ', "all"' : ""}) | getRaw(${reference}) | ${node.aggregator}())`;
      }
      case "filteredTraversal": {
         const reference = serializeAttributeReference(node.attribute);
         return `(self | downUntil(${quotedExpressionName(node.relationType)}, ${reference}) | whereAttribute(${serializeAttributeReference(node.filterAttribute)}, ${serializePipeExpression(node.filterValue)}) | getRaw(${reference}) | ${node.aggregator}())`;
      }
      case "parentLookup": {
         const reference = serializeAttributeReference(node.attribute);
         return `(self | upUntil(${quotedExpressionName(node.relationType)}, ${reference}) | getRaw(${reference}) | firstOrZero())`;
      }
      case "parentTraversal": {
         const reference = serializeAttributeReference(node.attribute);
         return `(self | upWith(${quotedExpressionName(node.parentRelationType)}, ${quotedExpressionName(node.relationType)}) | relatedValues(${quotedExpressionName(node.relationType)}, ${reference}) | ${node.aggregator === "single" ? "singleOrZero" : node.aggregator}())`;
      }
   }
}

function containsLegacyTraversal(node: ExpressionNode): boolean {
   if (["traversal", "filteredTraversal", "parentLookup", "parentTraversal"].includes(node.type)) return true;
   if (node.type === "binary") return containsLegacyTraversal(node.left) || containsLegacyTraversal(node.right);
   if (node.type === "conditional") return [node.condition, node.whenTrue, node.whenFalse].some(containsLegacyTraversal);
   if (node.type === "function") return node.args.some(containsLegacyTraversal);
   if (node.type === "pipe") return containsLegacyTraversal(node.input) || node.args.some(containsLegacyTraversal);
   return false;
}

export function migratePLAExpression(expression: string): string {
   const node = parsePLAExpression(expression);
   if (!containsLegacyTraversal(node)) return expression;
   const migrated = serializePipeExpression(node);
   parsePLAExpression(migrated);
   return migrated;
}

function findAttributeDefinitionId(reference: string, definitions: Record<string, { name: string }>): string | undefined {
   return isElementNameAttribute(reference) ? ELEMENT_NAME_ATTRIBUTE : Object.entries(definitions).find(([attributeId, definition]) => attributeId === reference || definition.name === reference)?.[0];
}

function findRelationTypeDefinitionId(reference: string, project: PLAProject): string | undefined {
   if (reference === "outgoing" || reference === "incoming") return reference;
   return Object.entries(project.relationTypes || {}).find(([relationTypeId, relationType]) => relationTypeId === reference || relationType.name === reference)?.[0];
}

function collectStringExpressionReferences(node: StringExpressionNode, attributes: string[]) {
   if (node.type === "attribute") attributes.push(node.name);
   if (node.type === "concat") {
      collectStringExpressionReferences(node.left, attributes);
      collectStringExpressionReferences(node.right, attributes);
   }
}

function collectAttributeReference(attribute: AttributeReference, references: { attributes: string[]; relationTypes: string[]; traversals: { relationType: string; attribute: AttributeReference }[] }) {
   if (attribute.type === "localAttribute") references.attributes.push(attribute.name);
   if (attribute.type === "stringExpression") collectStringExpressionReferences(attribute.expression, references.attributes);
}

function collectExpressionReferences(node: ExpressionNode, references: { attributes: string[]; relationTypes: string[]; traversals: { relationType: string; attribute: AttributeReference }[] }) {
   switch (node.type) {
      case "pipe": {
         collectExpressionReferences(node.input, references);
         if (["down", "up", "descendants", "ancestors", "downUntil", "upUntil", "upWith", "relatedValues"].includes(node.name)) {
            if (node.args[0]?.type === "string") references.relationTypes.push(node.args[0].value);
            else if (node.args[0]) collectExpressionReferences(node.args[0], references);
            if (node.name === "upWith" && node.args[1]?.type === "string") references.relationTypes.push(node.args[1].value);
         }
         node.args.forEach((arg, index) => {
            if (arg.type === "string" && ((node.name === "get" || node.name === "getRaw" || node.name === "whereAttribute") && index === 0 || ["downUntil", "upUntil", "relatedValues"].includes(node.name) && index === 1)) {
               references.traversals.push({ relationType: "outgoing", attribute: { type: "literal", name: arg.value } });
            } else if (node.name === "where") {
               const targetReferences = { attributes: [] as string[], relationTypes: [] as string[], traversals: [] as { relationType: string; attribute: AttributeReference }[] };
               collectExpressionReferences(arg, targetReferences);
               references.relationTypes.push(...targetReferences.relationTypes);
               references.traversals.push(...targetReferences.traversals, ...targetReferences.attributes.map((name) => ({ relationType: "outgoing", attribute: { type: "literal" as const, name } })));
            } else {
               collectExpressionReferences(arg, references);
            }
         });
         return;
      }
      case "string":
      case "cubeLiteral":
         return;
      case "attribute":
         references.attributes.push(node.name);
         return;
      case "binary":
         collectExpressionReferences(node.left, references);
         collectExpressionReferences(node.right, references);
         return;
      case "conditional":
         collectExpressionReferences(node.condition, references);
         collectExpressionReferences(node.whenTrue, references);
         collectExpressionReferences(node.whenFalse, references);
         return;
      case "function":
         node.args.forEach((arg) => collectExpressionReferences(arg, references));
         return;
      case "traversal":
         collectAttributeReference(node.attribute, references);
         references.traversals.push({ relationType: node.relationType, attribute: node.attribute });
         return;
      case "filteredTraversal":
         collectAttributeReference(node.attribute, references);
         collectAttributeReference(node.filterAttribute, references);
         collectExpressionReferences(node.filterValue, references);
         references.traversals.push({ relationType: node.relationType, attribute: node.attribute });
         references.traversals.push({ relationType: node.relationType, attribute: node.filterAttribute });
         return;
      case "parentLookup":
         collectAttributeReference(node.attribute, references);
         references.traversals.push({ relationType: node.relationType, attribute: node.attribute });
         return;
      case "parentTraversal":
         references.relationTypes.push(node.parentRelationType);
         collectAttributeReference(node.attribute, references);
         references.traversals.push({ relationType: node.relationType, attribute: node.attribute });
         return;
      default:
         return;
   }
}

function validateExpressionReferences(node: ExpressionNode, context: PLAExpressionValidationContext): string | undefined {
   const project = context.project;
   if (!project) return undefined;
   const validateToCube = (candidate: ExpressionNode): string | undefined => {
      if (candidate.type === "pipe") {
         if (candidate.name === "toCube") {
            const valueArgument = candidate.args[candidate.args.length - 1];
            if (valueArgument?.type !== "string" || !valueArgument.value.trim()) return "toCube requires literal dimension and value attribute names";
            for (let index = 0; index < candidate.args.length - 1; index += 2) {
               const dimensionArgument = candidate.args[index];
               if (dimensionArgument.type !== "string" || !dimensionArgument.value.trim()) return "toCube requires literal dimension and attribute names";
               findCubeDimensionIdByReference(dimensionArgument.value.trim(), project);
               const sourceArgument = candidate.args[index + 1];
               const sourceAttribute = sourceArgument.type === "string" ? sourceArgument.value.trim() : undefined;
               const sourceLiteral = sourceArgument.type === "function" && sourceArgument.name === "literal" &&
                  sourceArgument.args.length === 1 && sourceArgument.args[0].type === "string"
                  ? sourceArgument.args[0].value
                  : undefined;
               if (sourceLiteral !== undefined) continue;
               if (!sourceAttribute) return "toCube source must be an attribute name or literal(\"value\")";
               if (!Object.values(project.elementTypes).some((type) => findAttributeDefinitionId(sourceAttribute, type.attributes || {}))) {
                  return `Unknown toCube source attribute: ${sourceAttribute}`;
               }
            }
            const valueAttribute = valueArgument.value.trim();
            const valueDefinitions = Object.values(project.elementTypes).flatMap((type) => {
               const id = findAttributeDefinitionId(valueAttribute, type.attributes || {});
               return id ? [type.attributes[id]] : [];
            });
            if (!valueDefinitions.length) return `Unknown toCube value attribute: ${valueAttribute}`;
            if (!valueDefinitions.some((definition) => isPLANumberValueType(definition.valueType))) return `toCube value attribute must be numeric: ${valueAttribute}`;
         }
         const inputError = validateToCube(candidate.input);
         if (inputError) return inputError;
         for (const argument of candidate.args) {
            const argumentError = validateToCube(argument);
            if (argumentError) return argumentError;
         }
      } else if (candidate.type === "binary") {
         return validateToCube(candidate.left) || validateToCube(candidate.right);
      } else if (candidate.type === "conditional") {
         return validateToCube(candidate.condition) || validateToCube(candidate.whenTrue) || validateToCube(candidate.whenFalse);
      } else if (candidate.type === "function") {
         for (const argument of candidate.args) {
            const argumentError = validateToCube(argument);
            if (argumentError) return argumentError;
         }
      }
      return undefined;
   };
   const toCubeError = validateToCube(node);
   if (toCubeError) return toCubeError;
   const references = { attributes: [] as string[], relationTypes: [] as string[], traversals: [] as { relationType: string; attribute: AttributeReference }[] };
   collectExpressionReferences(node, references);

   const elementType = context.elementType || (context.elementTypeId ? project.elementTypes?.[context.elementTypeId] : undefined);
   if (elementType) {
      for (const attributeName of references.attributes) {
         if (!findAttributeDefinitionId(attributeName, elementType.attributes || {})) return `Unknown attribute: ${attributeName}`;
      }
   }

   for (const relationType of references.relationTypes) {
      if (!findRelationTypeDefinitionId(relationType, project)) return `Unknown relation type: ${relationType}`;
   }

   for (const traversal of references.traversals) {
      if (!findRelationTypeDefinitionId(traversal.relationType, project)) return `Unknown relation type: ${traversal.relationType}`;
      if (traversal.attribute.type === "localAttribute") continue;
      const traversalAttributeName = traversal.attribute.type === "stringExpression" ? staticStringExpressionValue(traversal.attribute.expression) : traversal.attribute.name;
      if (traversalAttributeName === undefined) continue;
      const searchableElementTypes = [context.elementType, ...Object.values(project.elementTypes || {})].filter(Boolean) as PLAElementTypeDefinition[];
      const attributeExists = searchableElementTypes.some((type) => Boolean(findAttributeDefinitionId(traversalAttributeName, type.attributes || {})));
      if (!attributeExists) return `Unknown traversal attribute: ${traversalAttributeName}`;
   }
   return undefined;
}

function getAttributeDefinitionValueType(reference: string, elementType: PLAElementTypeDefinition | undefined): PLAValueType | undefined {
   if (isElementNameAttribute(reference)) return "string";
   if (!elementType) return undefined;
   const attributeId = findAttributeDefinitionId(reference, elementType.attributes || {});
   return attributeId ? elementType.attributes?.[attributeId]?.valueType : undefined;
}

function getTraversalAttributeValueType(reference: string, context: PLAExpressionValidationContext): PLAValueType | undefined {
   const project = context.project;
   if (!project) return undefined;
   const matchingTypes = [context.elementType, ...Object.values(project.elementTypes || {})].filter(Boolean) as PLAElementTypeDefinition[];
   const valueTypes = Array.from(new Set(matchingTypes.map((type) => getAttributeDefinitionValueType(reference, type)).filter(Boolean)));
   return valueTypes.length === 1 ? valueTypes[0] : undefined;
}

function staticAttributeReferenceValueType(attribute: AttributeReference, context: PLAExpressionValidationContext): PLAValueType | undefined {
   if (attribute.type === "localAttribute") return undefined;
   const attributeName = attribute.type === "stringExpression" ? staticStringExpressionValue(attribute.expression) : attribute.name;
   return attributeName === undefined ? undefined : getTraversalAttributeValueType(attributeName, context);
}

function inferExpressionValueType(node: ExpressionNode, context: PLAExpressionValidationContext): PLAValueType | undefined {
   const elementType = context.elementType || (context.elementTypeId ? context.project?.elementTypes?.[context.elementTypeId] : undefined);
   switch (node.type) {
      case "pipe":
         if (node.name === "hasValue") return "boolean";
         if (node.name === "count" || node.name === "countBy") return "number";
         if (node.name === "coalesce") {
            const types = Array.from(new Set([node.input, ...node.args].map((arg) => inferExpressionValueType(arg, context)).filter(Boolean)));
            return types.length === 1 ? types[0] : undefined;
         }
         if ((node.name === "get" || node.name === "getRaw") && node.args[0]?.type === "string") return getTraversalAttributeValueType(node.args[0].value, context);
         return inferExpressionValueType(node.input, context);
      case "number": return "number";
      case "string": return "string";
      case "attribute": return getAttributeDefinitionValueType(node.name, elementType);
      case "binary": {
         const leftType = inferExpressionValueType(node.left, context);
         const rightType = inferExpressionValueType(node.right, context);
         if (["+", "-"].includes(node.operator) && leftType === rightType && isPLANumberValueType(leftType)) return leftType;
         if (["+", "-", "*", "/"].includes(node.operator)) return "number";
         return "boolean";
      }
      case "conditional": {
         const trueType = inferExpressionValueType(node.whenTrue, context);
         const falseType = inferExpressionValueType(node.whenFalse, context);
         return trueType === falseType ? trueType : undefined;
      }
      case "function": {
         if (node.name === "unitCube") return "number";
         if (node.name === "concat") return "string";
         if (node.name === "hasValue") return "boolean";
         if (node.name === "count") return "number";
         if (node.name === "firstValue" || node.name === "coalesce") {
            const valueTypes = Array.from(new Set(node.args.map((arg) => inferExpressionValueType(arg, context)).filter(Boolean)));
            return valueTypes.length === 1 ? valueTypes[0] : undefined;
         }
         const valueTypes = Array.from(new Set(node.args.map((arg) => inferExpressionValueType(arg, context)).filter((valueType) => isPLANumberValueType(valueType))));
         return valueTypes.length === 1 ? valueTypes[0] : "number";
      }
      case "traversal": return node.aggregator === "count" ? "number" : staticAttributeReferenceValueType(node.attribute, context);
      case "filteredTraversal": return node.aggregator === "count" ? "number" : staticAttributeReferenceValueType(node.attribute, context);
      case "parentLookup": return staticAttributeReferenceValueType(node.attribute, context);
      case "parentTraversal": return node.aggregator === "count" ? "number" : staticAttributeReferenceValueType(node.attribute, context);
      default: return undefined;
   }
}

function collectExpressionWarnings(node: ExpressionNode, context: PLAExpressionValidationContext, warnings: string[]) {
   if (node.type === "pipe") {
      collectExpressionWarnings(node.input, context, warnings);
      node.args.forEach((arg) => collectExpressionWarnings(arg, context, warnings));
      return;
   }
   if (node.type === "binary") {
      collectExpressionWarnings(node.left, context, warnings);
      collectExpressionWarnings(node.right, context, warnings);
      if (["+", "-"].includes(node.operator)) {
         const leftType = inferExpressionValueType(node.left, context);
         const rightType = inferExpressionValueType(node.right, context);
         if (isPLANumberValueType(leftType) && isPLANumberValueType(rightType) && leftType !== rightType && leftType !== "number" && rightType !== "number") {
            warnings.push(`Adding or subtracting different number types: ${leftType} ${node.operator} ${rightType}`);
         }
      }
      return;
   }
   if (node.type === "conditional") {
      collectExpressionWarnings(node.condition, context, warnings);
      collectExpressionWarnings(node.whenTrue, context, warnings);
      collectExpressionWarnings(node.whenFalse, context, warnings);
      return;
   }
   if (node.type === "function") node.args.forEach((arg) => collectExpressionWarnings(arg, context, warnings));
   if (node.type === "filteredTraversal") collectExpressionWarnings(node.filterValue, context, warnings);
}

function staticStringExpressionValue(node: StringExpressionNode): string | undefined {
   if (node.type === "constant") return node.value;
   if (node.type === "attribute") return undefined;
   const left = staticStringExpressionValue(node.left);
   const right = staticStringExpressionValue(node.right);
   return left === undefined || right === undefined ? undefined : left + right;
}

function validatePipeReferences(node: ExpressionNode, context: PLAExpressionValidationContext): string[] | undefined {
   const project = context.project;
   const localType = context.elementType || (context.elementTypeId ? project?.elementTypes[context.elementTypeId] : undefined);
   if (node.type === "attribute") {
      const id = localType && findAttributeDefinitionId(node.name, localType.attributes);
      const definition = id ? localType?.attributes[id] : undefined;
      return definition?.type === "cube" ? (definition.cubeDimensions || []).map((dimension) => dimension.elementTypeId) : undefined;
   }
   if (node.type === "binary") {
      validatePipeReferences(node.left, context);
      validatePipeReferences(node.right, context);
   }
   if (node.type === "conditional") {
      validatePipeReferences(node.condition, context);
      validatePipeReferences(node.whenTrue, context);
      validatePipeReferences(node.whenFalse, context);
   }
   if (node.type === "function") {
      if (node.name === "unitCube") {
         const names = unitCubeDimensionNames(node.args);
         if (!project) return undefined;
         const dimensions = names.map((name) => findCubeDimensionIdByReference(name, project));
         if (new Set(dimensions).size !== dimensions.length) throw new Error("unitCube cannot use the same dimension more than once");
         return dimensions;
      }
      if (node.name === "literal") throw new Error("literal(...) is only supported as a toCube source");
      node.args.forEach((arg) => validatePipeReferences(arg, context));
   }
   if (node.type !== "pipe") return undefined;
   const dimensions = validatePipeReferences(node.input, context);
   node.args.forEach((arg) => {
      if (node.name === "toCube" && arg.type === "function" && arg.name === "literal") return;
      validatePipeReferences(arg, node.name === "where" ? { project } : context);
   });
   if (["down", "up", "descendants", "ancestors", "downUntil", "upUntil", "upWith", "get", "getRaw", "relatedValues", "whereAttribute"].includes(node.name)) {
      const count = ["downUntil", "upUntil", "upWith", "relatedValues"].includes(node.name) ? 2 : 1;
      node.args.slice(0, count).forEach((arg) => {
         const type = inferExpressionValueType(arg, context);
         if (type !== undefined && type !== "string") throw new Error(`${node.name} requires string names`);
         if (arg.type === "string" && !arg.value.trim()) throw new Error(`${node.name} requires non-empty names`);
      });
   }
   if (node.name === "toCube") {
      for (let index = 0; index < node.args.length; index++) {
         const argument = node.args[index];
         if (index === node.args.length - 1 || index % 2 === 0) {
            if (argument.type !== "string" || !argument.value.trim()) throw new Error("toCube requires literal dimension and value attribute names");
         } else if (argument.type === "string") {
            if (!argument.value.trim()) throw new Error("toCube requires non-empty source attribute names");
         } else if (argument.type !== "function" || argument.name !== "literal" || argument.args.length !== 1 ||
            argument.args[0].type !== "string" || !argument.args[0].value.trim()) {
            throw new Error("toCube source must be an attribute name or literal(\"value\")");
         }
      }
   }
   if (node.name === "downUntil" && node.args[2]?.type === "string" && node.args[2].value !== "all") throw new Error('downUntil third argument must be "all"');
   if (node.name === "reciprocal") {
      const type = inferExpressionValueType(node.args[0], context);
      if (type !== undefined && !isPLANumberValueType(type)) throw new Error("reciprocal requires a finite numeric zero replacement");
   }
   if (node.name === "get" && node.args[0]?.type === "string" && project) {
      const reference = node.args[0].value;
      const cubes = Object.values(project.elementTypes).flatMap((type) => {
         const id = findAttributeDefinitionId(reference, type.attributes);
         const definition = id ? type.attributes[id] : undefined;
         return definition?.type === "cube" ? [(definition.cubeDimensions || []).map((dimension) => dimension.elementTypeId)] : [];
      });
      return cubes.length && cubes.every((candidate) => JSON.stringify(candidate) === JSON.stringify(cubes[0])) ? cubes[0] : undefined;
   }
   if (node.name === "toCube" && project) {
      const dimensions = node.args
         .filter((_argument, index) => index < node.args.length - 1 && index % 2 === 0)
         .map((argument) => {
            if (argument.type !== "string") throw new Error("toCube requires literal dimension and attribute names");
            return findCubeDimensionIdByReference(argument.value.trim(), project);
         });
      if (new Set(dimensions).size !== dimensions.length) throw new Error("toCube cannot use the same dimension more than once");
      return dimensions;
   }
   if (dimensions && ["slice", "select", "sumBy", "avgBy", "minBy", "maxBy", "countBy"].includes(node.name)) {
      const cube: PLAExpressionCube = { cells: {}, dimensions };
      if (node.name.endsWith("By") && node.args.length === 0) return dimensions;
      const references = node.name.endsWith("By") ? node.args : [node.args[0]];
      const indexes = references.map((arg) => arg.type === "number" || arg.type === "string" ? cubeDimensionIndex(cube, arg.value, context) : undefined);
      if (indexes.some((index) => index === undefined)) return undefined;
      if (node.name.endsWith("By")) {
         if (new Set(indexes).size !== indexes.length) throw new Error("Duplicate summary dimension");
         return indexes.flatMap((index) => index === undefined ? [] : [dimensions[index]]);
      }
      return node.name === "slice" ? dimensions.filter((_dimension, index) => index !== indexes[0]) : dimensions;
   }
   if (dimensions && node.name === "cell" && node.args.length !== dimensions.length) throw new Error(`cell requires ${dimensions.length} coordinates`);
   return ["select", "coalesce", "reciprocal"].includes(node.name) ? dimensions : undefined;
}

export function getPLAExpressionCubeDimensions(expression: string, context: PLAExpressionValidationContext): string[] | undefined {
   return validatePipeReferences(parsePLAExpression(expression), context);
}

export function validatePLAExpression(expression: string, context: PLAExpressionValidationContext = {}): { valid: boolean; error?: string; warnings?: string[] } {
   try {
      const node = parsePLAExpression(expression);
      validatePipeReferences(node, context);
      const referenceError = validateExpressionReferences(node, context);
      if (referenceError) return { valid: false, error: referenceError };
      const warnings: string[] = [];
      collectExpressionWarnings(node, context, warnings);
      return warnings.length ? { valid: true, warnings } : { valid: true };
   } catch (error: any) {
      return { valid: false, error: error.message || String(error) };
   }
}

function numeric(value: any): number {
   if (value === true) return 1;
   if (value === false || value === undefined || value === null || value === "") return 0;
   const numberValue = Number(value);
   return Number.isFinite(numberValue) ? numberValue : 0;
}

function hasValidValue(value: any): boolean {
   if (value === undefined || value === null || value === "") return false;
   if (typeof value === "number" && !Number.isFinite(value)) return false;
   return true;
}

function resolveAttributeValue(attribute: AttributeValue | undefined, calculated: Record<string, any>, name: string): any {
   if (Object.prototype.hasOwnProperty.call(calculated, name)) return calculated[name];
   if (!attribute) return undefined;
   if (attribute.mode === "constant") return attribute.value;
   return undefined;
}

function findAttributeIdByReference(reference: string, context: PLAEvaluationContext, element: PLAElementRecord = context.element): string {
   if (Object.prototype.hasOwnProperty.call(element.attributes, reference)) return reference;
   const definitions = context.project?.elementTypes[element.elementType]?.attributes || {};
   return Object.entries(definitions).find(([attributeId, definition]) => attributeId === reference || definition.name === reference)?.[0] || reference;
}

function findRelationTypeIdByReference(reference: string, context: PLAEvaluationContext): string {
   if (reference === "outgoing" || reference === "incoming") return reference;
   const relationTypes = context.project?.relationTypes || {};
   return Object.entries(relationTypes).find(([relationTypeId, relationType]) => relationTypeId === reference || relationType.name === reference)?.[0] || reference;
}

function findCubeDimensionIdByReference(reference: string, project: PLAProject): string {
   const matches = Object.entries(project.elementTypes).flatMap(([elementTypeId, elementType]) => elementTypeId === reference || elementType.name === reference ? [elementTypeId] : []);
   const uniqueMatches = Array.from(new Set(matches));
   if (uniqueMatches.length === 1) return uniqueMatches[0];
   if (uniqueMatches.length > 1) throw new Error(`Ambiguous cube dimension: ${reference}; use its element type ID`);
   throw new Error(`Unknown cube dimension: ${reference}`);
}

function findCubeMemberIdByValue(dimensionId: string, value: unknown, context: PLAEvaluationContext): string {
   if (typeof value !== "string" || !value.trim()) throw new Error(`Cube source attribute for dimension ${dimensionId} must contain a member ID or name`);
   const member = cubeMember({ cells: {}, dimensions: [dimensionId] }, 0, value.trim(), context);
   return member;
}

function resolveAttributeReference(reference: string, context: PLAEvaluationContext): any {
   if (isElementNameAttribute(reference)) return context.element.name;
   const attributeId = findAttributeIdByReference(reference, context);
   const definition = context.project?.elementTypes[context.element.elementType]?.attributes?.[attributeId];
   if (definition?.type === "expression") {
      if (Object.prototype.hasOwnProperty.call(context.calculated || {}, attributeId)) return context.calculated?.[attributeId];
      if (context.resolveCalculatedAttribute) return context.resolveCalculatedAttribute(context.element, attributeId);
      if (context.project) return resolvePLACalculatedAttributes(context.element, context.project, context.elements, context.relations)[attributeId];
   }
   return resolveAttributeValue(context.element.attributes[attributeId], context.calculated || {}, attributeId);
}

function compare(operator: string, left: any, right: any) {
   switch (operator) {
      case ">": return numeric(left) > numeric(right);
      case "<": return numeric(left) < numeric(right);
      case ">=": return numeric(left) >= numeric(right);
      case "<=": return numeric(left) <= numeric(right);
      case "==": return left == right;
      case "!=": return left != right;
      default: throw new Error(`Unsupported operator '${operator}'`);
   }
}

function valueType(value: any): string {
   if (value === null) return "null";
   if (value === undefined) return "undefined";
   if (Array.isArray(value)) return "array";
   return typeof value;
}

function displayReferenceValue(value: string, context: PLAEvaluationContext): string {
   const element = getElementsById(context.elements).get(value);
   if (element) return elementLabel(element);
   const elementType = context.project?.elementTypes?.[value];
   if (elementType?.name) return elementType.name;
   const relationType = context.project?.relationTypes?.[value];
   if (relationType?.name) return relationType.name;
   return value;
}

function escapeRegExp(value: string) {
   return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function sanitizeExplanationText(value: string, context: PLAEvaluationContext): string {
   const replacements = [
      ...context.elements.map((element) => [element.id, elementLabel(element)] as const),
      ...Object.entries(context.project?.elementTypes || {}).map(([elementTypeId, elementType]) => [elementTypeId, elementType.name] as const),
      ...Object.entries(context.project?.relationTypes || {}).map(([relationTypeId, relationType]) => [relationTypeId, relationType.name] as const)
   ].filter(([id, label]) => Boolean(id && label && id !== label)).sort((left, right) => right[0].length - left[0].length);
   return replacements.reduce((text, [id, label]) => text.replace(new RegExp(escapeRegExp(id), "g"), label), value);
}

function formatValue(value: any, context?: PLAEvaluationContext): string {
   if (isPipeElements(value)) return `[${value.elements.map(elementLabel).join(", ")}]`;
   if (isPipeValues(value)) return `[${value.entries.map((entry) => `${elementLabel(entry.element)}: ${formatValue(entry.value, context)}`).join(", ")}]`;
   if (isExpressionCube(value)) return JSON.stringify({
      dimensions: value.dimensions.map((id) => context?.project?.elementTypes[id]?.name || id),
      cells: Object.entries(value.cells).map(([key, cellValue]) => ({
         members: cubeCoordinates(key, value).map((id) => {
            if (!context || id === PLA_CUBE_TOTAL_KEY) return id;
            const element = getElementsById(context.elements).get(id);
            return element ? elementLabel(element) : "Unknown element";
         }),
         value: cellValue
      }))
   });
   if (typeof value === "string") return `"${context ? displayReferenceValue(value, context) : value}"`;
   if (value === undefined) return "undefined";
   return String(value);
}

function elementLabel(element: PLAElementRecord): string {
   return element.name || "Unnamed element";
}

function elementTypeLabel(elementTypeId: string, context: PLAEvaluationContext): string {
   return context.project?.elementTypes?.[elementTypeId]?.name || elementTypeId;
}

function relationTypeLabel(relationType: string, context: PLAEvaluationContext): string {
   if (relationType === "outgoing" || relationType === "incoming") return relationType;
   return context.project?.relationTypes?.[relationType]?.name || relationType;
}

function attributeLabel(reference: string, context: PLAEvaluationContext, element: PLAElementRecord = context.element): string {
   const attributeId = findAttributeIdByReference(reference, context, element);
   const definition = context.project?.elementTypes?.[element.elementType]?.attributes?.[attributeId];
   if (definition?.name) return definition.name;
   const matchingDefinition = Object.values(context.project?.elementTypes || {})
      .flatMap((elementType) => Object.entries(elementType.attributes || {}))
      .find(([attributeId, definition]) => attributeId === reference || definition.name === reference)?.[1];
   return matchingDefinition?.name || reference;
}

function attributeReferenceLabel(attribute: AttributeReference, context?: PLAEvaluationContext): string {
   if (attribute.type === "localAttribute") return `[${context ? attributeLabel(attribute.name, context) : attribute.name}]`;
   if (attribute.type === "stringExpression") return `[${stringExpressionLabel(attribute.expression, context)}]`;
   return context ? attributeLabel(attribute.name, context) : attribute.name;
}

function stringExpressionLabel(node: StringExpressionNode, context?: PLAEvaluationContext): string {
   switch (node.type) {
      case "constant": return `"${node.value}"`;
      case "attribute": return `[${context ? attributeLabel(node.name, context) : node.name}]`;
      case "concat": return `${stringExpressionLabel(node.left, context)} + ${stringExpressionLabel(node.right, context)}`;
      default: return "string expression";
   }
}

function nodeLabel(node: ExpressionNode, context?: PLAEvaluationContext): string {
   switch (node.type) {
      case "self": return "self";
      case "pipe": return `${nodeLabel(node.input, context)} | ${node.name}(${node.args.map((arg) => nodeLabel(arg, context)).join(", ")})`;
      case "number": return String(node.value);
      case "string": return `"${context ? displayReferenceValue(node.value, context) : node.value}"`;
      case "cubeLiteral": return serializeCubeLiteral(node, (name) => `"${name}"`);
      case "attribute": return `[${context ? attributeLabel(node.name, context) : node.name}]`;
      case "binary": return `${nodeLabel(node.left, context)} ${node.operator} ${nodeLabel(node.right, context)}`;
      case "conditional": return `${nodeLabel(node.condition, context)} ? ${nodeLabel(node.whenTrue, context)} : ${nodeLabel(node.whenFalse, context)}`;
      case "function": return `${node.name}(${node.args.map((arg) => nodeLabel(arg, context)).join(", ")})`;
      case "traversal": return `${node.functionName || node.aggregator}(${context ? relationTypeLabel(node.relationType, context) : node.relationType}.${attributeReferenceLabel(node.attribute, context)})`;
      case "filteredTraversal": return `${node.functionName}(${context ? relationTypeLabel(node.relationType, context) : node.relationType}.${attributeReferenceLabel(node.attribute, context)}, ${attributeReferenceLabel(node.filterAttribute, context)}, ${nodeLabel(node.filterValue, context)})`;
      case "parentLookup": return `parent(${context ? relationTypeLabel(node.relationType, context) : node.relationType}.${attributeReferenceLabel(node.attribute, context)})`;
      case "parentTraversal": return `parent${node.aggregator}(${context ? relationTypeLabel(node.parentRelationType, context) : node.parentRelationType}.${context ? relationTypeLabel(node.relationType, context) : node.relationType}.${attributeReferenceLabel(node.attribute, context)})`;
      default: return "expression";
   }
}

function explanationStep(label: string, value: any, details?: string[], children?: PLAExpressionExplanationStep[]): PLAExpressionExplanationStep {
   return { label, value, valueType: valueType(value), details, children };
}

function formatExplanationLines(step: PLAExpressionExplanationStep, context: PLAEvaluationContext, depth = 0): string[] {
   const indent = "  ".repeat(depth);
   const lines = [`${indent}${sanitizeExplanationText(step.label, context)} = ${formatValue(step.value, context)} (${step.valueType})`];
   step.details?.forEach((detail) => lines.push(`${indent}  ${sanitizeExplanationText(detail, context)}`));
   step.children?.forEach((child) => lines.push(...formatExplanationLines(child, context, depth + 1)));
   return lines;
}

function explainAttributeReference(reference: string, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   const attributeId = findAttributeIdByReference(reference, context);
   const definition = context.project?.elementTypes[context.element.elementType]?.attributes?.[attributeId];
   const value = resolveAttributeReference(reference, context);
   return explanationStep(`[${attributeLabel(reference, context)}]`, value, [
      `Element: ${elementLabel(context.element)}`,
      `Definition type: ${definition?.type || "unknown"}`,
      `Raw value type: ${valueType(value)}`
   ]);
}

function explainTraversalAttributeReference(attribute: AttributeReference, context: PLAEvaluationContext): { attributeName: string; step?: PLAExpressionExplanationStep } {
   if (attribute.type === "literal") return { attributeName: attribute.name };
   if (attribute.type === "stringExpression") {
      const step = explainStringExpression(attribute.expression, context);
      if (typeof step.value !== "string" || !step.value.trim()) throw new Error(`Cannot evaluate [${stringExpressionLabel(attribute.expression, context)}] as traversal attribute name: expected a non-empty string`);
      return { attributeName: step.value.trim(), step };
   }
   const step = explainAttributeReference(attribute.name, context);
   if (typeof step.value !== "string" || !step.value.trim()) throw new Error(`Cannot evaluate [${attributeLabel(attribute.name, context)}] as traversal attribute name: expected a non-empty string`);
   return { attributeName: step.value.trim(), step };
}

function explainStringExpression(node: StringExpressionNode, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   switch (node.type) {
      case "constant": return explanationStep(stringExpressionLabel(node, context), node.value);
      case "attribute": return explainAttributeReference(node.name, context);
      case "concat": {
         const left = explainStringExpression(node.left, context);
         const right = explainStringExpression(node.right, context);
         const value = `${stringExpressionValue(left.value, stringExpressionLabel(node.left, context))}${stringExpressionValue(right.value, stringExpressionLabel(node.right, context))}`;
         return explanationStep(stringExpressionLabel(node, context), value, ["Operator: +"], [left, right]);
      }
      default: throw new Error("Unsupported string expression node");
   }
}

function explainAggregateValues(name: string, values: any[], label: string): any {
   return aggregateValues(name, values, label);
}

function explainTraversal(aggregator: string, relationType: string, attributeName: string, context: PLAEvaluationContext, deep = false, functionName = aggregator): PLAExpressionExplanationStep {
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const traversal = collectTraversalTargetValues(relationTypeId, attributeName, context, deep);
   const valueSteps = traversal.values.map(({ element, value }) => explanationStep(`${attributeLabelText} on ${elementLabel(element)}`, value, [`Element type: ${elementTypeLabel(element.elementType, context)}`]));
   const values = valueSteps.map((step) => step.value);
   const result = explainAggregateValues(aggregator, values, `${functionName}(${relationLabel}.${attributeLabelText})`);
   return explanationStep(`${functionName}(${relationLabel}.${attributeLabelText})`, result, [
      `Start element: ${elementLabel(context.element)}`,
      `Traversal mode: ${deep ? "deep" : "stop at matching attribute"}`,
      `Visited elements: ${traversal.visitedCount}`,
      `Related values: ${values.length}`
   ], valueSteps);
}

function explainFilteredTraversal(aggregator: string, relationType: string, attributeName: string, filterAttributeName: string, filterValue: any, context: PLAEvaluationContext, functionName: string): PLAExpressionExplanationStep {
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const filterAttributeLabelText = attributeLabel(filterAttributeName, context);
   const traversal = collectTraversalTargetValues(relationTypeId, attributeName, context);
   const matchedValues = traversal.values.filter(({ element }) => elementMatchesFilter(element, filterAttributeName, filterValue, context));
   const valueSteps = matchedValues.map(({ element, value }) => {
      const elementFilterValue = getElementAttributeValue(element, filterAttributeName, context).value;
      return explanationStep(`${attributeLabelText} on ${elementLabel(element)}`, value, [
         `Element type: ${elementTypeLabel(element.elementType, context)}`,
         `Filter: ${filterAttributeLabelText} == ${formatValue(elementFilterValue, context)}`
      ]);
   });
   const values = valueSteps.map((step) => step.value);
   const result = explainAggregateValues(aggregator, values, `${functionName}(${relationLabel}.${attributeLabelText}, ${filterAttributeLabelText}, ${formatValue(filterValue, context)})`);
   return explanationStep(`${functionName}(${relationLabel}.${attributeLabelText}, ${filterAttributeLabelText}, ${formatValue(filterValue, context)})`, result, [
      `Start element: ${elementLabel(context.element)}`,
      `Traversal mode: stop at matching attribute`,
      `Visited elements: ${traversal.visitedCount}`,
      `Filter value: ${formatValue(filterValue, context)}`,
      `Related values: ${values.length}`
   ], valueSteps);
}

function explainParentLookup(relationType: string, attributeName: string, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   if (!context.project) throw new Error("Cannot evaluate parent lookup without project definitions");
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const pending = [context.element.id];
   const visited = new Set<string>([context.element.id]);
   const inspected: PLAExpressionExplanationStep[] = [];

   while (pending.length > 0) {
      const currentId = pending.shift()!;
      const parentRelations = context.relations.filter((relation) => relation.toId === currentId && relation.relationType === relationTypeId);
      for (const relation of parentRelations) {
         if (visited.has(relation.fromId)) continue;
         visited.add(relation.fromId);
         const parentElement = context.elements.find((element) => element.id === relation.fromId);
         if (!parentElement) throw new Error(`Cannot evaluate parent(${relationLabel}.${attributeLabelText}): missing parent element`);
         const result = getElementAttributeValue(parentElement, attributeName, context);
         inspected.push(explanationStep(`Inspect parent ${elementLabel(parentElement)}`, result.value, [`Attribute found: ${result.found}`]));
         if (result.found) return explanationStep(`parent(${relationLabel}.${attributeLabelText})`, result.value, [], inspected);
         pending.push(parentElement.id);
      }
   }

   return explanationStep(`parent(${relationLabel}.${attributeLabelText})`, 0, [`No parent path with attribute ${attributeLabelText} found`], inspected);
}

function explainParentTraversal(aggregator: string, parentRelationType: string, relationType: string, attributeName: string, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   if (!context.project) throw new Error("Cannot evaluate parent traversal without project definitions");
   const parentRelationTypeId = findRelationTypeIdByReference(parentRelationType, context);
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const parentRelationLabel = relationTypeLabel(parentRelationTypeId, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const pending = [context.element.id];
   const visited = new Set<string>([context.element.id]);
   const inspected: PLAExpressionExplanationStep[] = [];

   while (pending.length > 0) {
      const currentId = pending.shift()!;
      const parentRelations = context.relations.filter((relation) => relation.toId === currentId && relation.relationType === parentRelationTypeId);
      for (const parentRelation of parentRelations) {
         if (visited.has(parentRelation.fromId)) continue;
         visited.add(parentRelation.fromId);
         const parentElement = context.elements.find((element) => element.id === parentRelation.fromId);
         if (!parentElement) throw new Error(`Cannot evaluate parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText}): missing parent element`);

         const targetRelations = context.relations.filter((relation) => relation.fromId === parentElement.id && relation.relationType === relationTypeId);
         if (targetRelations.length > 0) {
            const valueSteps = targetRelations.map((relation) => {
               const targetElement = context.elements.find((element) => element.id === relation.toId);
               if (!targetElement) throw new Error(`Cannot evaluate parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText}): missing related element`);
               const value = getElementAttributeValue(targetElement, attributeName, context).value;
               return explanationStep(`${attributeLabelText} on ${elementLabel(targetElement)}`, value);
            });
            const values = valueSteps.map((step) => step.value);
            const result = explainAggregateValues(aggregator, values, `parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText})`);
            return explanationStep(`parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText})`, result, [
               `Matched parent: ${elementLabel(parentElement)}`
            ], [...inspected, ...valueSteps]);
         }

         inspected.push(explanationStep(`Inspect parent ${elementLabel(parentElement)}`, "no related values"));
         pending.push(parentElement.id);
      }
   }

   return explanationStep(`parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText})`, 0, [`No parent path with relation ${relationLabel} found`], inspected);
}

function explainNode(node: ExpressionNode, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   switch (node.type) {
      case "self": return explanationStep("self", elementSelection([context.element]));
      case "pipe": {
         const children: PLAExpressionExplanationStep[] = [];
         const result = evaluatePipe(node, context, (arg, argContext) => {
            try {
               const step = explainNode(arg, argContext);
               children.push(step);
               return step.value;
            } catch (error) {
               children.push(explanationStep(nodeLabel(arg, argContext), undefined, [`Error: ${error instanceof Error ? error.message : String(error)}`]));
               throw error;
            }
         });
         return explanationStep(nodeLabel(node, context), result, [`Pipe operation: ${node.name}`], children);
      }
      case "number": return explanationStep(nodeLabel(node, context), node.value);
      case "string": return explanationStep(nodeLabel(node, context), node.value);
      case "cubeLiteral": return explanationStep(nodeLabel(node, context), evaluateCubeLiteral(node, context));
      case "attribute": return explainAttributeReference(node.name, context);
      case "binary": {
         const left = explainNode(node.left, context);
         const right = explainNode(node.right, context);
         let value: any;
         if ([">", "<", ">=", "<=", "==", "!="].includes(node.operator)) value = compare(node.operator, left.value, right.value);
         else if (["+", "-", "*", "/"].includes(node.operator)) value = evaluateArithmetic(node.operator, operandCube(node.left, left.value, context), operandCube(node.right, right.value, context), context);
         else throw new Error(`Unsupported operator '${node.operator}'`);
         return explanationStep(nodeLabel(node, context), value, [`Operator: ${node.operator}`], [left, right]);
      }
      case "conditional": {
         const condition = explainNode(node.condition, context);
         const selected = condition.value ? explainNode(node.whenTrue, context) : explainNode(node.whenFalse, context);
         return explanationStep(nodeLabel(node, context), selected.value, [`Condition result: ${formatValue(condition.value, context)}`], [condition, selected]);
      }
      case "function": {
         if (node.name === "unitCube") return explanationStep(nodeLabel(node, context), evaluateUnitCube(node.args, context));
         if (node.name === "concat") {
            const args = node.args.map((arg) => explainNode(arg, context));
            return explanationStep(nodeLabel(node, context), args.map((arg) => stringExpressionValue(arg.value, "concat")).join(""), undefined, args);
         }
         if (node.name === "hasValue") {
            if (node.args.length !== 1) throw new Error(`Cannot evaluate hasValue(...): expected exactly one argument but found ${node.args.length}`);
            try {
               const arg = explainNode(node.args[0], context);
               return explanationStep(nodeLabel(node, context), hasValidValue(arg.value), [`Function: ${node.name}`], [arg]);
            } catch (error: any) {
               return explanationStep(nodeLabel(node, context), false, [`Function: ${node.name}`, `Blocked error: ${error.message || error}`]);
            }
         }
         if (node.name === "firstValue" || node.name === "coalesce") {
            const steps: PLAExpressionExplanationStep[] = [];
            for (const arg of node.args) {
               try {
                  const step = explainNode(arg, context);
                  steps.push(step);
                  if (hasValidValue(step.value)) return explanationStep(nodeLabel(node, context), step.value, [`Function: ${node.name}`], steps);
               } catch (error: any) {
                  steps.push(explanationStep(nodeLabel(arg, context), undefined, [`Blocked error: ${error.message || error}`]));
               }
            }
            return explanationStep(nodeLabel(node, context), undefined, [`Function: ${node.name}`, "No argument returned a value"], steps);
         }
         const args = node.args.map((arg) => explainNode(arg, context));
         if (node.name === "round") {
            if (args.length !== 1) throw new Error(`Cannot evaluate round(...): expected exactly one argument but found ${args.length}`);
            const value = node.args[0].type === "attribute"
               ? cubeAttributeValue(args[0].value, node.args[0].name, context.element, context)
               : args[0].value;
            return explanationStep(nodeLabel(node, context), roundCubeValue(value), [`Function: ${node.name}`], args);
         }
         const result = explainAggregateValues(node.name, args.map((arg) => numeric(arg.value)), `${node.name}(...)`);
         return explanationStep(nodeLabel(node, context), result, [`Function: ${node.name}`], args);
      }
      case "traversal": {
         const attribute = explainTraversalAttributeReference(node.attribute, context);
         const traversal = explainTraversal(node.aggregator, node.relationType, attribute.attributeName, context, Boolean(node.deep), node.functionName || node.aggregator);
         return attribute.step ? explanationStep(nodeLabel(node, context), traversal.value, [`Resolved traversal attribute: ${attributeLabel(attribute.attributeName, context)}`], [attribute.step, traversal]) : traversal;
      }
      case "filteredTraversal": {
         const attribute = explainTraversalAttributeReference(node.attribute, context);
         const filterAttribute = explainTraversalAttributeReference(node.filterAttribute, context);
         const filterValue = explainNode(node.filterValue, context);
         const traversal = explainFilteredTraversal(node.aggregator, node.relationType, attribute.attributeName, filterAttribute.attributeName, filterValue.value, context, node.functionName);
         const steps = [attribute.step, filterAttribute.step, filterValue, traversal].filter(Boolean) as PLAExpressionExplanationStep[];
         const details = [`Resolved traversal attribute: ${attributeLabel(attribute.attributeName, context)}`, `Resolved filter attribute: ${attributeLabel(filterAttribute.attributeName, context)}`];
         return explanationStep(nodeLabel(node, context), traversal.value, details, steps);
      }
      case "parentLookup": {
         const attribute = explainTraversalAttributeReference(node.attribute, context);
         const lookup = explainParentLookup(node.relationType, attribute.attributeName, context);
         return attribute.step ? explanationStep(nodeLabel(node, context), lookup.value, [`Resolved traversal attribute: ${attributeLabel(attribute.attributeName, context)}`], [attribute.step, lookup]) : lookup;
      }
      case "parentTraversal": {
         const attribute = explainTraversalAttributeReference(node.attribute, context);
         const traversal = explainParentTraversal(node.aggregator, node.parentRelationType, node.relationType, attribute.attributeName, context);
         return attribute.step ? explanationStep(nodeLabel(node, context), traversal.value, [`Resolved traversal attribute: ${attributeLabel(attribute.attributeName, context)}`], [attribute.step, traversal]) : traversal;
      }
      default: throw new Error("Unsupported expression node");
   }
}

function evaluateNode(node: ExpressionNode, context: PLAEvaluationContext): any {
   switch (node.type) {
      case "self": return elementSelection([context.element]);
      case "pipe": return evaluatePipe(node, context, evaluateNode);
      case "number": return node.value;
      case "string": return node.value;
      case "cubeLiteral": return evaluateCubeLiteral(node, context);
      case "attribute": return resolveAttributeReference(node.name, context);
      case "binary": {
         const left = evaluateNode(node.left, context);
         const right = evaluateNode(node.right, context);
         if ([">", "<", ">=", "<=", "==", "!="].includes(node.operator)) return compare(node.operator, left, right);
         if (["+", "-", "*", "/"].includes(node.operator)) return evaluateArithmetic(node.operator, operandCube(node.left, left, context), operandCube(node.right, right, context), context);
         throw new Error(`Unsupported operator '${node.operator}'`);
      }
      case "conditional": return evaluateNode(node.condition, context) ? evaluateNode(node.whenTrue, context) : evaluateNode(node.whenFalse, context);
      case "function": {
         if (node.name === "unitCube") return evaluateUnitCube(node.args, context);
         if (node.name === "concat") return node.args.map((arg) => stringExpressionValue(evaluateNode(arg, context), "concat")).join("");
         if (node.name === "hasValue") {
            if (node.args.length !== 1) throw new Error(`Cannot evaluate hasValue(...): expected exactly one argument but found ${node.args.length}`);
            try {
               return hasValidValue(evaluateNode(node.args[0], context));
            } catch {
               return false;
            }
         }
         if (node.name === "firstValue" || node.name === "coalesce") {
            for (const arg of node.args) {
               try {
                  const value = evaluateNode(arg, context);
                  if (hasValidValue(value)) return value;
               } catch {
                  // Keep searching for the first argument that can produce a usable value.
               }
            }
            return undefined;
         }
         if (node.name === "round") {
            if (node.args.length !== 1) throw new Error(`Cannot evaluate round(...): expected exactly one argument but found ${node.args.length}`);
            const argument = node.args[0];
            let value = evaluateNode(argument, context);
            if (argument.type === "attribute") value = cubeAttributeValue(value, argument.name, context.element, context);
            return roundCubeValue(value);
         }
         if (node.name === "literal") throw new Error("literal(...) is only supported as a toCube source");
         return aggregateValues(node.name, node.args.map((arg) => numeric(evaluateNode(arg, context))), `${node.name}(...)`);
      }
      case "traversal": return evaluateTraversal(node.aggregator, node.relationType, resolveTraversalAttributeReference(node.attribute, context), context, Boolean(node.deep), node.functionName || node.aggregator);
      case "filteredTraversal": return evaluateFilteredTraversal(node.aggregator, node.relationType, resolveTraversalAttributeReference(node.attribute, context), resolveTraversalAttributeReference(node.filterAttribute, context), evaluateNode(node.filterValue, context), context, node.functionName);
      case "parentLookup": return evaluateParentLookup(node.relationType, resolveTraversalAttributeReference(node.attribute, context), context);
      case "parentTraversal": return evaluateParentTraversal(node.aggregator, node.parentRelationType, node.relationType, resolveTraversalAttributeReference(node.attribute, context), context);
      default: throw new Error("Unsupported expression node");
   }
}

export function evaluatePLAExpression(expression: string, context: PLAEvaluationContext): any {
   return evaluateNode(parsePLAExpression(expression), context);
}

export function explainPLAExpression(expression: string, context: PLAEvaluationContext): PLAExpressionExplanation {
   const parsed = parsePLAExpression(expression);
   const root = explainNode(parsed, context);
   return { expression: sanitizeExplanationText(nodeLabel(parsed, context), context), result: root.value, valueType: root.valueType, root, lines: formatExplanationLines(root, context) };
}

export function simulatePLAExpression(expression: string, context: PLAEvaluationContext): PLAExpressionExplanation {
   if (!expression.trim()) throw new Error("Expression is required");
   const elements = context.elements.some((element) => element.id === context.element.id)
      ? context.elements.map((element) => element.id === context.element.id ? context.element : element)
      : [...context.elements, context.element];
   return explainPLAExpression(expression, { ...context, elements, calculated: undefined });
}

type PipeElements = { kind: "elements"; elements: PLAElementRecord[] };
type PipeValues = { kind: "values"; entries: { element: PLAElementRecord; value: unknown }[]; raw?: boolean; emptyCube?: PLAExpressionCube };
type ToCubeLiteral = { kind: "toCubeLiteral"; value: string };
export type PLAExpressionCube = { cells: Record<string, unknown>; dimensions: string[]; valueType?: PLAValueType; totalDimensions?: number[]; selectedMembers?: (string[] | undefined)[] };
type NodeEvaluator = (node: ExpressionNode, context: PLAEvaluationContext) => unknown;

const pipeArities: Record<string, [number, number]> = {
   down: [1, 1], up: [1, 1], descendants: [1, 1], ancestors: [1, 1],
   downUntil: [2, 3], upUntil: [2, 2], upWith: [2, 2], relatedValues: [2, 2],
   get: [1, 1], getRaw: [1, 1], where: [1, 1], whereAttribute: [2, 2],
   sum: [0, 0], avg: [0, 0], min: [0, 0], max: [0, 0], count: [0, 0],
   single: [0, 0], singleOrZero: [0, 0], firstOrZero: [0, 0],
   round: [0, 0], reciprocal: [1, 1], hasValue: [0, 0], coalesce: [1, Infinity],
   slice: [2, 2], select: [2, Infinity], cell: [0, Infinity],
   sumBy: [0, Infinity], avgBy: [0, Infinity], minBy: [0, Infinity], maxBy: [0, Infinity], countBy: [0, Infinity],
   toCube: [3, Infinity]
};

/** Pipe operations with their argument counts, e.g. "upWith(2)" or "coalesce(1+)". */
export function getPLAPipeFunctionSignatures(): string[] {
   return Object.entries(pipeArities).map(([name, [min, max]]) => `${name}(${min === max ? min : max === Infinity ? `${min}+` : `${min}-${max}`})`);
}

function validatePipeArity(name: string, count: number) {
   const arity = Object.prototype.hasOwnProperty.call(pipeArities, name) ? pipeArities[name] : undefined;
   if (!arity) throw new Error(`Unknown pipe operation: ${name}`);
   if (count < arity[0] || count > arity[1]) throw new Error(`Invalid arguments for ${name}: expected ${arity[0] === arity[1] ? arity[0] : `${arity[0]} or more`}, found ${count}`);
   if (name === "toCube" && count % 2 === 0) throw new Error("toCube requires dimension/source-attribute pairs followed by one value attribute");
}

function isObject(value: unknown): value is Record<string, unknown> {
   return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPipeElements(value: unknown): value is PipeElements {
   return isObject(value) && value.kind === "elements" && Array.isArray(value.elements) && value.elements.every(isPipeElement);
}

function isPipeValues(value: unknown): value is PipeValues {
   return isObject(value) && value.kind === "values" && Array.isArray(value.entries) && value.entries.every((entry) => isObject(entry) && isPipeElement(entry.element));
}

function isPipeElement(value: unknown): value is PLAElementRecord {
   return isObject(value) && value.recordType === "ELEMENT" && typeof value.id === "string" && typeof value.elementType === "string" && typeof value.name === "string" && isObject(value.attributes);
}

function isExpressionCube(value: unknown): value is PLAExpressionCube {
   return isObject(value) && isObject(value.cells) && Array.isArray(value.dimensions) && value.dimensions.every((dimension) => typeof dimension === "string");
}

function elementSelection(elements: PLAElementRecord[]): PipeElements {
   return { kind: "elements", elements: Array.from(new Map(elements.map((element) => [element.id, element])).values()) };
}

function requireElements(value: unknown, operation: string): PLAElementRecord[] {
   if (!isPipeElements(value)) throw new Error(`${operation} requires an element selection`);
   return value.elements;
}

function pipeName(value: unknown, operation: string): string {
   if (typeof value !== "string" || !value.trim()) throw new Error(`${operation} requires a non-empty name`);
   return value;
}

function cubeAttributeValue(value: unknown, reference: string, element: PLAElementRecord, context: PLAEvaluationContext): unknown {
   if (isExpressionCube(value)) return value;
   const attributeId = findAttributeIdByReference(reference, context, element);
   const definition = context.project?.elementTypes[element.elementType]?.attributes?.[attributeId];
   if (definition?.type !== "cube") return value;
   if (value !== undefined && (!isObject(value) || value.cells !== undefined && !isObject(value.cells))) throw new Error(`Invalid cube value: ${reference}`);
   const cells = isObject(value) && isObject(value.cells) ? value.cells : {};
   return { cells, dimensions: (definition.cubeDimensions || []).map((dimension) => dimension.elementTypeId), valueType: definition.valueType } satisfies PLAExpressionCube;
}

function navigation(elements: PLAElementRecord[], relationName: string, incoming: boolean, deep: boolean, context: PLAEvaluationContext): PipeElements {
   const relationTypeId = findRelationTypeIdByReference(relationName, context);
   if (context.project && !findRelationTypeDefinitionId(relationName, context.project)) throw new Error(`Unknown relation type: ${relationName}`);
   const directionalRelationTypeId = relationTypeId === "incoming" || relationTypeId === "outgoing" ? incoming ? "incoming" : "outgoing" : relationTypeId;
   const roots = new Set(elements.map((element) => element.id));
   const visited = deep ? new Set(roots) : new Set<string>();
   const pending = [...elements];
   const result: PLAElementRecord[] = [];
   for (let index = 0; index < pending.length; index++) {
      const current = pending[index];
      for (const relation of getRelatedRelations(context, current.id, directionalRelationTypeId, incoming ? "incoming" : "outgoing")) {
         const id = incoming ? relation.fromId : relation.toId;
         if (id === current.id || visited.has(id)) continue;
         visited.add(id);
         const target = getElementsById(context.elements).get(id);
         if (!target) throw new Error(`Cannot evaluate navigation: missing related element ${id}`);
         result.push(target);
         if (deep) pending.push(target);
      }
   }
   return elementSelection(result);
}

function nearestAncestor(element: PLAElementRecord, relationName: string, test: (element: PLAElementRecord) => boolean, context: PLAEvaluationContext): PLAElementRecord[] {
   if (!context.project) throw new Error("Cannot evaluate ancestor search without project definitions");
   const relationTypeId = findRelationTypeIdByReference(relationName, context);
   const pending = [element];
   const visited = new Set([element.id]);
   for (let index = 0; index < pending.length; index++) {
      for (const relation of getRelatedRelations(context, pending[index].id, relationTypeId, "incoming")) {
         if (visited.has(relation.fromId)) continue;
         visited.add(relation.fromId);
         const parent = getElementsById(context.elements).get(relation.fromId);
         if (!parent) throw new Error("Cannot evaluate ancestor search: missing parent element");
         if (test(parent)) return [parent];
         pending.push(parent);
      }
   }
   return [];
}

function requireCube(value: unknown, operation: string): PLAExpressionCube {
   const cube = isPipeValues(value) && value.entries.length === 1 ? value.entries[0].value : value;
   if (!isExpressionCube(cube)) throw new Error(`${operation} requires a cube with dimension definitions`);
   for (const key of Object.keys(cube.cells)) {
      if (cube.dimensions.length === 0 ? key !== "" : key.split(PLA_CUBE_KEY_SEPARATOR).length !== cube.dimensions.length) throw new Error(`Invalid cube cell coordinate: ${key}`);
   }
   return cube;
}

function requireCubes(value: unknown, operation: string): PLAExpressionCube[] {
   const cubes = isPipeValues(value)
      ? value.entries.length ? value.entries.map((entry) => requireCube(entry.value, operation)) : [requireCube(value.emptyCube, operation)]
      : [requireCube(value, operation)];
   const first = cubes[0];
   if (cubes.some((cube) => cube.valueType !== first.valueType
      || cube.dimensions.length !== first.dimensions.length
      || cube.dimensions.some((dimension, index) => dimension !== first.dimensions[index])
      || cube.dimensions.some((_dimension, index) => Boolean(cube.totalDimensions?.includes(index)) !== Boolean(first.totalDimensions?.includes(index))))) {
      throw new Error(`${operation} requires cubes with matching dimensions, value types and total selections`);
   }
   return cubes;
}

function mapNumericCube(cube: PLAExpressionCube, context: PLAEvaluationContext, operation: string, transform: (value: number) => number): PLAExpressionCube {
   const entries = new Map([...Object.entries(cube.cells), ...cubeCalculationEntries(cube, context)]);
   const cells = Object.fromEntries(Array.from(entries, ([key, rawValue]) => {
      const value = rawValue === undefined ? 0 : rawValue;
      if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`${operation} requires finite numeric cube cells`);
      const result = transform(value);
      if (!Number.isFinite(result)) throw new Error(`${operation} produced a non-finite cube cell`);
      return [key, result];
   }));
   return { ...cube, cells };
}

function roundCube(cube: PLAExpressionCube): PLAExpressionCube {
   const cells = Object.fromEntries(Object.entries(cube.cells).map(([key, value]) => {
      if (typeof value !== "number" || !Number.isFinite(value)) return [key, value];
      return [key, Math.round(value)];
   }));
   return { ...cube, cells };
}

function reciprocalCubeValue(value: unknown, zeroReplacement: unknown, context: PLAEvaluationContext): unknown {
   if (typeof zeroReplacement !== "number" || !Number.isFinite(zeroReplacement)) throw new Error("reciprocal requires a finite numeric zero replacement");
   requireCubes(value, "reciprocal");
   const transform = (cube: PLAExpressionCube) => mapNumericCube(cube, context, "reciprocal", (cell) => cell === 0 ? zeroReplacement : 1 / cell);
   if (isPipeValues(value)) {
      return {
         ...value,
         entries: value.entries.map((entry) => ({ ...entry, value: transform(requireCube(entry.value, "reciprocal")) })),
         emptyCube: value.emptyCube ? transform(value.emptyCube) : undefined
      } satisfies PipeValues;
   }
   return transform(requireCube(value, "reciprocal"));
}

function roundCubeValue(value: unknown): unknown {
   if (isExpressionCube(value)) return roundCube(requireCube(value, "round"));
   if (isPipeValues(value)) {
      const containsCube = Boolean(value.emptyCube) || value.entries.some((entry) => isExpressionCube(entry.value));
      if (containsCube) {
         requireCubes(value, "round");
         return {
            ...value,
            entries: value.entries.map((entry) => ({ ...entry, value: roundCube(requireCube(entry.value, "round")) })),
            emptyCube: value.emptyCube ? roundCube(value.emptyCube) : undefined
         } satisfies PipeValues;
      }
   }
   return aggregateValues("round", [value], "round(pipe)");
}

function emptyCubeSelection(reference: string, context: PLAEvaluationContext): PLAExpressionCube | undefined {
   const definitions = Object.values(context.project?.elementTypes || {}).flatMap((type) => {
      const id = findAttributeDefinitionId(reference, type.attributes);
      return id ? [type.attributes[id]] : [];
   });
   if (!definitions.length || definitions.some((definition) => definition.type !== "cube")) return undefined;
   const cubes = definitions.map((definition) => ({
      cells: {},
      dimensions: (definition.cubeDimensions || []).map((dimension) => dimension.elementTypeId),
      valueType: definition.valueType
   }));
   const first = cubes[0];
   return cubes.every((cube) => cube.valueType === first.valueType && JSON.stringify(cube.dimensions) === JSON.stringify(first.dimensions)) ? first : undefined;
}

function cubeDimensionIndex(cube: PLAExpressionCube, reference: unknown, context: Pick<PLAEvaluationContext, "project">): number {
   if (typeof reference === "number" && Number.isInteger(reference) && reference >= 0 && reference < cube.dimensions.length) return reference;
   if (typeof reference === "string") {
      const matches = cube.dimensions.flatMap((id, index) => id === reference || context.project?.elementTypes[id]?.name === reference ? [index] : []);
      if (matches.length === 1) return matches[0];
      if (matches.length > 1) throw new Error(`Ambiguous cube dimension: ${reference}; use its zero-based index`);
   }
   throw new Error(`Unknown cube dimension: ${String(reference)}`);
}

function cubeMember(cube: PLAExpressionCube, dimensionIndex: number, value: unknown, context: PLAEvaluationContext): string {
   const name = pipeName(value, "cube selector");
   if (name === PLA_CUBE_TOTAL_KEY) return name;
   const dimension = cube.dimensions[dimensionIndex];
   const candidates = getElementsByType(context.elements, dimension);
   const byId = candidates.find((element) => element.id === name);
   if (byId) return byId.id;
   const byName = candidates.filter((element) => element.name === name);
   if (byName.length === 1) return byName[0].id;
   if (byName.length > 1) throw new Error(`Ambiguous cube member: ${name}; use its element ID`);
   throw new Error(`Unknown cube member: ${name}`);
}

function cubeCoordinates(key: string, cube: PLAExpressionCube): string[] {
   return cube.dimensions.length === 0 ? [] : key.split(PLA_CUBE_KEY_SEPARATOR);
}

function cubeDetailEntries(cube: PLAExpressionCube): [string, unknown][] {
   return Object.entries(cube.cells).filter(([key]) => cubeCoordinates(key, cube).every((coordinate, index) => coordinate !== PLA_CUBE_TOTAL_KEY || cube.totalDimensions?.includes(index)));
}

function cubeCalculationEntries(cube: PLAExpressionCube, context: PLAEvaluationContext): [string, unknown][] {
   const detailEntries = cubeDetailEntries(cube);
   const members = cube.dimensions.map((dimension, index) => {
      if (cube.selectedMembers?.[index]) return cube.selectedMembers[index]!;
      if (cube.totalDimensions?.includes(index)) return [PLA_CUBE_TOTAL_KEY];
      return [...new Set([
         ...getElementsByType(context.elements, dimension).map((element) => element.id),
         ...detailEntries.map(([key]) => cubeCoordinates(key, cube)[index])
      ])];
   });
   const coordinates = members.reduce<string[][]>((rows, values) =>
      rows.flatMap((row) => values.map((value) => [...row, value])), [[]]);
   return coordinates.map((keys) => {
      const key = getPLACubeCellKey(keys);
      return [key, cube.cells[key] === undefined ? 0 : cube.cells[key]];
   });
}

function summarizeCubeValues(name: string, values: unknown[]): number {
   if (name === "count") return values.length;
   const numbers = values.map((value) => {
      if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`${name} requires finite numeric cube cells`);
      return value;
   });
   return aggregateValues(name, numbers, `${name}(cube)`);
}

function unitCubeDimensionNames(args: ExpressionNode[]): string[] {
   if (!args.length) throw new Error("unitCube requires at least one dimension");
   return args.map((arg) => {
      if (arg.type !== "string" || !arg.value.trim()) throw new Error("unitCube requires literal non-empty dimension names or IDs");
      return arg.value.trim();
   });
}

function evaluateUnitCube(args: ExpressionNode[], context: PLAEvaluationContext): PLAExpressionCube {
   const names = unitCubeDimensionNames(args);
   if (!context.project) throw new Error("unitCube requires a project");
   const dimensions = names.map((name) => findCubeDimensionIdByReference(name, context.project!));
   if (new Set(dimensions).size !== dimensions.length) throw new Error("unitCube cannot use the same dimension more than once");
   const cube: PLAExpressionCube = { cells: {}, dimensions, valueType: "number" };
   return { ...cube, cells: Object.fromEntries(cubeCalculationEntries(cube, context).map(([key]) => [key, 1])) };
}

function evaluateCubeLiteral(node: Extract<ExpressionNode, { type: "cubeLiteral" }>, context: PLAEvaluationContext): PLAExpressionCube {
   const elementTypes = context.project?.elementTypes;
   if (!elementTypes) throw new Error("Cube literals require a project");
   const normalize = (value: string) => value.trim().toLowerCase();
   const dimensions = node.dimensions.map((name) => {
      const entry = Object.entries(elementTypes).find(([id]) => id === name) || Object.entries(elementTypes).find(([, type]) => normalize(type.name) === normalize(name));
      if (!entry) throw new Error(`Unknown dimension '${name}' in cube literal`);
      return entry[0];
   });
   const cells: Record<string, unknown> = Object.create(null);
   for (const cell of node.cells) {
      const members = cell.members.map((member, index) => {
         if (member === PLA_CUBE_TOTAL_KEY) return member;
         const candidates = getElementsByType(context.elements, dimensions[index]);
         const match = candidates.find((element) => element.id === member) || candidates.find((element) => normalize(element.name) === normalize(member));
         if (!match) throw new Error(`Unknown member '${member}' of dimension '${node.dimensions[index]}' in cube literal`);
         return match.id;
      });
      cells[getPLACubeCellKey(members)] = cell.value;
   }
   return { cells, dimensions, valueType: "number" };
}

function operandCube(node: ExpressionNode, value: unknown, context: PLAEvaluationContext): unknown {
   return node.type === "attribute" ? cubeAttributeValue(value, node.name, context.element, context) : value;
}

function arithmeticCube(value: unknown): PLAExpressionCube | undefined {
   const candidate = isPipeValues(value) && value.entries.length === 1 ? value.entries[0].value : value;
   return isExpressionCube(candidate) ? requireCube(candidate, "arithmetic") : undefined;
}

function applyArithmetic(operator: string, left: number, right: number): number {
   if (operator === "+") return left + right;
   if (operator === "-") return left - right;
   if (operator === "*") return left * right;
   return left / right;
}

function cubeCellNumber(value: unknown, operator: string): number {
   if (value === undefined) return 0;
   if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`Operator '${operator}' requires finite numeric cube cells`);
   return value;
}

// Cube arithmetic: cube op number, number op cube, or cube op cube matched on shared dimensions.
function evaluateArithmetic(operator: string, left: unknown, right: unknown, context: PLAEvaluationContext): unknown {
   const leftCube = arithmeticCube(left);
   const rightCube = arithmeticCube(right);
   if (!leftCube && !rightCube) return applyArithmetic(operator, numeric(left), numeric(right));
   if (!["+", "-", "*", "/"].includes(operator)) throw new Error(`Operator '${operator}' is not supported for cubes`);
   if (!leftCube || !rightCube) {
      const cube = (leftCube || rightCube)!;
      const scalar = numeric(leftCube ? right : left);
      const cells: Record<string, unknown> = Object.create(null);
      for (const [key, value] of cubeCalculationEntries(cube, context)) {
         const cell = cubeCellNumber(value, operator);
         cells[key] = leftCube ? applyArithmetic(operator, cell, scalar) : applyArithmetic(operator, scalar, cell);
      }
      return { ...cube, cells } satisfies PLAExpressionCube;
   }
   // Dimensions match by id, falling back to name; members match by id, or by name when the dimensions differ.
   const dimensionName = (id: string) => (context.project?.elementTypes[id]?.name || id).trim().toLowerCase();
   const usedRight = new Set<number>();
   const shared: { index: number; other: number; byId: boolean }[] = [];
   leftCube.dimensions.forEach((dimension, index) => {
      let other = rightCube.dimensions.findIndex((candidate, position) => candidate === dimension && !usedRight.has(position));
      if (other < 0) other = rightCube.dimensions.findIndex((candidate, position) => dimensionName(candidate) === dimensionName(dimension) && !usedRight.has(position));
      if (other < 0) return;
      usedRight.add(other);
      shared.push({ index, other, byId: rightCube.dimensions[other] === dimension });
   });
   const elementsById = getElementsById(context.elements);
   const memberName = (id: string) => (elementsById.get(id)?.name || id).trim().toLowerCase();
   const memberKey = (pair: { byId: boolean }, id: string) => pair.byId || id === PLA_CUBE_TOTAL_KEY ? id : memberName(id);
   if (shared.length === 0) throw new Error(`Operator '${operator}' requires cubes sharing at least one dimension`);
   const extra = rightCube.dimensions.map((_dimension, index) => index).filter((index) => !shared.some((pair) => pair.other === index));
   const rightGroups = new Map<string, { coordinates: string[]; value: unknown }[]>();
   for (const [key, value] of cubeCalculationEntries(rightCube, context)) {
      const coordinates = cubeCoordinates(key, rightCube);
      const groupKey = getPLACubeCellKey(shared.map((pair) => memberKey(pair, coordinates[pair.other])));
      const group = rightGroups.get(groupKey);
      if (group) group.push({ coordinates, value });
      else rightGroups.set(groupKey, [{ coordinates, value }]);
   }
   const cells: Record<string, unknown> = Object.create(null);
   for (const [key, value] of cubeCalculationEntries(leftCube, context)) {
      const coordinates = cubeCoordinates(key, leftCube);
      const groupKey = getPLACubeCellKey(shared.map((pair) => memberKey(pair, coordinates[pair.index])));
      const partners = rightGroups.get(groupKey);
      if (!partners) continue;
      for (const partner of partners) {
         cells[getPLACubeCellKey([...coordinates, ...extra.map((index) => partner.coordinates[index])])] = applyArithmetic(operator, cubeCellNumber(value, operator), cubeCellNumber(partner.value, operator));
      }
   }
   return {
      cells,
      dimensions: [...leftCube.dimensions, ...extra.map((index) => rightCube.dimensions[index])],
      valueType: leftCube.valueType === rightCube.valueType ? leftCube.valueType : leftCube.valueType === "number" ? rightCube.valueType : leftCube.valueType,
      selectedMembers: [...leftCube.dimensions.map((_dimension, index) => leftCube.selectedMembers?.[index]), ...extra.map((index) => rightCube.selectedMembers?.[index])],
      totalDimensions: [...(leftCube.totalDimensions || []), ...extra.flatMap((index, position) => rightCube.totalDimensions?.includes(index) ? [leftCube.dimensions.length + position] : [])]
   } satisfies PLAExpressionCube;
}

function evaluateCubePipe(name: string, input: unknown, args: unknown[], context: PLAEvaluationContext): unknown {
   const cubes = requireCubes(input, name);
   const cube = cubes[0];
   if (isPipeValues(input) && input.entries.length !== 1 && ["slice", "select", "cell"].includes(name)) {
      const emptyResult = input.entries.length === 0 ? evaluateCubePipe(name, cube, args, context) : undefined;
      return {
         kind: "values",
         entries: input.entries.map((entry) => ({ ...entry, value: evaluateCubePipe(name, entry.value, args, context) })),
         emptyCube: input.entries.length === 0 && name !== "cell"
            ? requireCube(emptyResult, name)
            : undefined
      } satisfies PipeValues;
   }
   if (name === "slice" || name === "select") {
      const index = cubeDimensionIndex(cube, args[0], context);
      const members = new Set(args.slice(1).map((arg) => cubeMember(cube, index, arg, context)));
      const cells: Record<string, unknown> = Object.create(null);
      for (const [key, value] of Object.entries(cube.cells)) {
         const coordinates = cubeCoordinates(key, cube);
         if (!members.has(coordinates[index])) continue;
         cells[name === "slice" ? getPLACubeCellKey(coordinates.filter((_key, position) => position !== index)) : key] = value;
      }
      const totalDimensions = name === "slice"
         ? (cube.totalDimensions || []).filter((position) => position !== index).map((position) => position > index ? position - 1 : position)
         : Array.from(new Set([...(cube.totalDimensions || []).filter((position) => position !== index), ...(members.has(PLA_CUBE_TOTAL_KEY) ? [index] : [])]));
      if (members.has(PLA_CUBE_TOTAL_KEY) && members.size > 1) throw new Error("Select TOTAL separately from detail members to avoid double-counting");
      const selectedMembers = cube.dimensions.map((_dimension, position) => position === index ? [...members] : cube.selectedMembers?.[position]);
      return {
         ...cube, cells, dimensions: name === "slice" ? cube.dimensions.filter((_dimension, position) => position !== index) : cube.dimensions, totalDimensions,
         selectedMembers: name === "slice" ? selectedMembers.filter((_members, position) => position !== index) : selectedMembers
      };
   }
   if (name === "cell") {
      if (args.length !== cube.dimensions.length) throw new Error(`cell requires ${cube.dimensions.length} coordinates`);
      const keys = args.map((arg, index) => cubeMember(cube, index, arg, context));
      const value = cube.cells[getPLACubeCellKey(keys)];
      return value === undefined && isPLANumberValueType(cube.valueType || "number") ? 0 : value;
   }
   const indexes = args.length ? args.map((arg) => cubeDimensionIndex(cube, arg, context)) : cube.dimensions.map((_dimension, index) => index);
   if (new Set(indexes).size !== indexes.length) throw new Error("Duplicate summary dimension");
   const groups = new Map<string, unknown[]>();
   const summaryCubes = isPipeValues(input) && input.entries.length === 0 ? [] : cubes;
   for (const [key, value] of summaryCubes.flatMap((item) => cubeCalculationEntries(item, context))) {
      const coordinates = cubeCoordinates(key, cube);
      const groupKey = getPLACubeCellKey(indexes.map((index) => coordinates[index]));
      const values = groups.get(groupKey) || [];
      values.push(value);
      groups.set(groupKey, values);
   }
   const aggregator = name.slice(0, -2);
   return {
      cells: Object.fromEntries(Array.from(groups, ([key, values]) => [key, summarizeCubeValues(aggregator, values)])),
      dimensions: indexes.map((index) => cube.dimensions[index]),
      valueType: aggregator === "count" ? "number" : cube.valueType,
      totalDimensions: indexes.flatMap((index, position) => cube.totalDimensions?.includes(index) ? [position] : []),
      selectedMembers: indexes.map((index) => cube.selectedMembers?.[index])
   } satisfies PLAExpressionCube;
}

function evaluateToCube(input: unknown, args: unknown[], context: PLAEvaluationContext): PipeValues {
   const elements = requireElements(input, "toCube");
   const project = context.project;
   if (!project) throw new Error("toCube requires project definitions");
   if (args.length < 3 || args.length % 2 === 0) throw new Error("toCube requires dimension/source-attribute pairs followed by one value attribute");
   const valueAttribute = pipeName(args[args.length - 1], "toCube").trim();
   const dimensionReferences = args.filter((_argument, index) => index < args.length - 1 && index % 2 === 0)
      .map((argument) => pipeName(argument, "toCube").trim());
   const dimensions = dimensionReferences.map((reference) => findCubeDimensionIdByReference(reference, project));
   if (new Set(dimensions).size !== dimensions.length) throw new Error("toCube cannot use the same dimension more than once");

   const allValueDefinitions = Object.values(project.elementTypes).flatMap((elementType) => {
      const attributeId = findAttributeDefinitionId(valueAttribute, elementType.attributes || {});
      return attributeId ? [elementType.attributes[attributeId]] : [];
   });
   const valueTypes = Array.from(new Set(allValueDefinitions.map((definition) => definition.valueType).filter((type): type is PLAValueType => Boolean(type))));
   const emptyCubeValueType = valueTypes.length === 1 ? valueTypes[0] : undefined;
   const entries = elements.map((element) => {
      const elementContext = { ...context, element, calculated: undefined };
      const cellCoordinates = dimensions.map((dimensionId, dimensionIndex) => {
         const source = args[dimensionIndex * 2 + 1];
         if (isObject(source) && source.kind === "toCubeLiteral" && typeof source.value === "string") {
            return findCubeMemberIdByValue(dimensionId, source.value, { ...elementContext, elements: context.elements });
         }
         const sourceAttribute = pipeName(source, "toCube").trim();
         const sourceValue = getElementAttributeValue(element, sourceAttribute, elementContext);
         if (!sourceValue.found) throw new Error(`Missing toCube source attribute ${sourceAttribute} on ${elementLabel(element)}`);
         return findCubeMemberIdByValue(dimensionId, sourceValue.value, { ...elementContext, elements: context.elements });
      });
      const value = getElementAttributeValue(element, valueAttribute, elementContext);
      const valueDefinitionId = findAttributeIdByReference(valueAttribute, elementContext, element);
      const valueDefinition = project.elementTypes[element.elementType]?.attributes[valueDefinitionId];
      if (!valueDefinition) throw new Error(`Missing toCube value attribute ${valueAttribute} on ${elementLabel(element)}`);
      const valueType = valueDefinition.valueType;
      if (!isPLANumberValueType(valueType)) throw new Error(`toCube value attribute must be numeric on ${elementLabel(element)}: ${valueAttribute}`);
      const numericValue = value.value === undefined || value.value === null || value.value === "" ? 0 : value.value;
      if (typeof numericValue !== "number" || !Number.isFinite(numericValue)) throw new Error(`toCube value attribute must contain a finite number on ${elementLabel(element)}: ${valueAttribute}`);
      return {
         element,
         value: { cells: { [getPLACubeCellKey(cellCoordinates)]: numericValue }, dimensions, valueType } satisfies PLAExpressionCube
      };
   });
   return {
      kind: "values",
      entries,
      emptyCube: { cells: {}, dimensions, valueType: emptyCubeValueType }
   };
}

function evaluatePipe(node: Extract<ExpressionNode, { type: "pipe" }>, context: PLAEvaluationContext, evaluate: NodeEvaluator): unknown {
   const { name, args } = node;
   if (name === "coalesce" || name === "hasValue") {
      // These are the language's explicit error-catching, lazy operations.
      const candidates = name === "coalesce" ? [node.input, ...args] : [node.input];
      for (const candidate of candidates) {
         try {
            const value = evaluate(candidate, context);
            if (hasValidValue(value)) return name === "hasValue" ? true : candidate.type === "attribute" ? cubeAttributeValue(value, candidate.name, context.element, context) : value;
         } catch {
            // Match legacy coalesce/hasValue semantics.
         }
      }
      return name === "hasValue" ? false : undefined;
   }
   let input = evaluate(node.input, context);
   if (node.input.type === "attribute") input = cubeAttributeValue(input, node.input.name, context.element, context);
   if (name === "where") {
      const elements = requireElements(input, name);
      return elementSelection(elements.filter((element) => Boolean(evaluate(args[0], { ...context, element, calculated: undefined }))));
   }
   const values = args.map((arg, index) => {
      if (name === "toCube" && index % 2 === 1 && index < args.length - 1 &&
         arg.type === "function" && arg.name === "literal" && arg.args.length === 1 && arg.args[0].type === "string") {
         return { kind: "toCubeLiteral", value: arg.args[0].value } satisfies ToCubeLiteral;
      }
      return evaluate(arg, context);
   });
   const nameAt = (index: number) => {
      const value = pipeName(values[index], name);
      return args[index].type === "string" ? value : value.trim();
   };
   if (name === "toCube") return evaluateToCube(input, values, context);
   if (["slice", "select", "cell", "sumBy", "avgBy", "minBy", "maxBy", "countBy"].includes(name)) return evaluateCubePipe(name, input, values, context);
   if (["down", "up", "descendants", "ancestors"].includes(name)) {
      return navigation(requireElements(input, name), nameAt(0), name === "up" || name === "ancestors", name === "descendants" || name === "ancestors", context);
   }
   if (name === "downUntil") {
      const attributeName = nameAt(1);
      const relationName = nameAt(0);
      const relationTypeId = findRelationTypeIdByReference(relationName, context);
      if (values.length === 3 && values[2] !== "all") throw new Error('downUntil third argument must be "all"');
      return elementSelection(requireElements(input, name).flatMap((element) => collectTraversalTargetValues(relationTypeId, attributeName, { ...context, element }, values[2] === "all").values.map((entry) => entry.element)));
   }
   if (name === "upUntil" || name === "upWith") {
      const relationName = nameAt(0);
      const reference = nameAt(1);
      return elementSelection(requireElements(input, name).flatMap((element) => nearestAncestor(element, relationName, (ancestor) => name === "upUntil"
         ? getElementAttributeValue(ancestor, reference, context).found
         : getRelatedRelations(context, ancestor.id, findRelationTypeIdByReference(reference, context), "outgoing").length > 0, context)));
   }
   if (name === "whereAttribute") {
      const reference = nameAt(0);
      return elementSelection(requireElements(input, name).filter((element) => elementMatchesFilter(element, reference, values[1], context)));
   }
   if (name === "relatedValues") {
      const relationTypeId = findRelationTypeIdByReference(nameAt(0), context);
      const reference = nameAt(1);
      const entries = requireElements(input, name).flatMap((element) => getRelatedRelations(context, element.id, relationTypeId, "outgoing").map((relation) => {
         const target = getElementsById(context.elements).get(relation.toId);
         if (!target) throw new Error("Cannot evaluate relatedValues: missing related element");
         return { element: target, value: getElementAttributeValue(target, reference, context).value };
      }));
      return { kind: "values", entries, raw: true } satisfies PipeValues;
   }
   if (name === "get" || name === "getRaw") {
      const reference = nameAt(0);
      if (!isPipeElements(input)) throw new Error("get requires an element selection");
      return { kind: "values", entries: input.elements.map((element) => ({
         element, value: name === "getRaw" ? getElementAttributeValue(element, reference, context).value : cubeAttributeValue(getElementAttributeValue(element, reference, context).value, reference, element, context)
      })), raw: name === "getRaw", emptyCube: name === "get" && input.elements.length === 0 ? emptyCubeSelection(reference, context) : undefined } satisfies PipeValues;
   }
   if (name === "single" || name === "singleOrZero" || name === "firstOrZero") {
      const entries = isPipeValues(input) ? input.entries.map((entry) => entry.value) : isPipeElements(input) ? input.elements : undefined;
      if (!entries) throw new Error(`${name} requires a selection`);
      if (name === "firstOrZero") return entries.length ? entries[0] : 0;
      if (name === "singleOrZero" && entries.length === 0) return 0;
      const selected = aggregateValues("single", entries, `${name}(selection)`);
      return isPipeElements(input) ? elementSelection([selected]) : selected;
   }
   if (name === "round") return roundCubeValue(input);
   if (name === "reciprocal") return reciprocalCubeValue(input, values[0], context);
   if (aggregateFunctions.includes(name)) {
      if (isExpressionCube(input) || isPipeValues(input) && !input.raw && (input.emptyCube || input.entries.some((entry) => isExpressionCube(entry.value)))) {
         const cubes = requireCubes(input, name);
         if (isPipeValues(input) && input.entries.length === 0) return 0;
         return summarizeCubeValues(name, cubes.flatMap((cube) => cubeCalculationEntries(cube, context)).map((entry) => entry[1]));
      }
      if (isPipeValues(input)) return aggregateValues(name, input.entries.map((entry) => entry.value), `${name}(selection)`);
      if (isPipeElements(input) && name === "count") return input.elements.length;
      if (isPipeElements(input)) throw new Error(`${name} requires selected attribute values; use get first`);
      return aggregateValues(name, [input], `${name}(pipe)`);
   }
   throw new Error(`Unknown pipe operation: ${name}`);
}

function aggregateValues(name: string, values: any[], label: string): any {
   if (name === "single") {
      if (values.length !== 1) throw new Error(`Cannot evaluate ${label}: expected exactly one related element but found ${values.length}`);
      return values[0];
   }
   if (name === "round") {
      if (values.length !== 1) throw new Error(`Cannot evaluate ${label}: expected exactly one argument but found ${values.length}`);
      return Math.round(numeric(values[0]));
   }
   if (values.length === 0) return 0;
   const numbers = values.map(numeric);
   switch (name) {
      case "sum": return numbers.reduce((acc, value) => acc + value, 0);
      case "avg": return numbers.reduce((acc, value) => acc + value, 0) / numbers.length;
      case "min": return Math.min(...numbers);
      case "max": return Math.max(...numbers);
      case "count": return numbers.length;
      default: throw new Error(`Unsupported function '${name}'`);
   }
}

function resolveTraversalAttributeReference(attribute: AttributeReference, context: PLAEvaluationContext): string {
   if (attribute.type === "literal") return attribute.name;
   if (attribute.type === "stringExpression") {
      const value = evaluateStringExpression(attribute.expression, context);
      if (!value.trim()) throw new Error(`Cannot evaluate [${stringExpressionLabel(attribute.expression, context)}] as traversal attribute name: expected a non-empty string`);
      return value.trim();
   }
   const value = resolveAttributeReference(attribute.name, context);
   if (typeof value !== "string" || !value.trim()) throw new Error(`Cannot evaluate [${attributeLabel(attribute.name, context)}] as traversal attribute name: expected a non-empty string`);
   return value.trim();
}

function stringExpressionValue(value: any, label: string): string {
   if (typeof value !== "string") throw new Error(`Cannot evaluate ${label} as string expression term: expected a string`);
   return value;
}

function evaluateStringExpression(node: StringExpressionNode, context: PLAEvaluationContext): string {
   switch (node.type) {
      case "constant": return node.value;
      case "attribute": return stringExpressionValue(resolveAttributeReference(node.name, context), `[${node.name}]`);
      case "concat": return evaluateStringExpression(node.left, context) + evaluateStringExpression(node.right, context);
      default: throw new Error("Unsupported string expression node");
   }
}

function collectTraversalTargetValues(relationTypeId: string, attributeName: string, context: PLAEvaluationContext, deep = false): { visitedCount: number; values: { element: PLAElementRecord; value: any }[] } {
   const targets = new Set<string>();
   const pending = [context.element.id];
   const visited = new Set<string>();
   while (pending.length > 0) {
      const currentId = pending.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      getRelatedRelations(context, currentId, relationTypeId, relationTypeId === "incoming" ? "incoming" : "outgoing")
         .forEach((relation) => {
            const targetId = relationTypeId === "incoming" ? relation.fromId : relation.toId;
            if (targetId === context.element.id || targets.has(targetId)) return;
            if (deep) {
               targets.add(targetId);
               pending.push(targetId);
               return;
            }
            const targetElement = getElementsById(context.elements).get(targetId);
            if (!targetElement) return;
            const result = getElementAttributeValue(targetElement, attributeName, context);
            if (result.found) {
               targets.add(targetId);
            } else if (!visited.has(targetId)) {
               pending.push(targetId);
            }
         });
   }

   const values = Array.from(targets)
      .map((targetId) => getElementsById(context.elements).get(targetId))
      .filter(Boolean)
      .map((element) => {
         const targetElement = element as PLAElementRecord;
         return { element: targetElement, value: getElementAttributeValue(targetElement, attributeName, context).value };
      });

   return { visitedCount: visited.size, values };
}

function elementMatchesFilter(element: PLAElementRecord, filterAttributeName: string, filterValue: any, context: PLAEvaluationContext): boolean {
   const result = getElementAttributeValue(element, filterAttributeName, context);
   return result.found && result.value == filterValue;
}

export function evaluateTraversal(aggregator: string, relationType: string, attributeName: string, context: PLAEvaluationContext, deep = false, functionName = aggregator): any {
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const values = collectTraversalTargetValues(relationTypeId, attributeName, context, deep).values.map((entry) => entry.value);
   return aggregateValues(aggregator, values, `${functionName}(${relationLabel}.${attributeLabelText})`);
}

function evaluateFilteredTraversal(aggregator: string, relationType: string, attributeName: string, filterAttributeName: string, filterValue: any, context: PLAEvaluationContext, functionName: string): any {
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const filterAttributeLabelText = attributeLabel(filterAttributeName, context);
   const values = collectTraversalTargetValues(relationTypeId, attributeName, context)
      .values
      .filter(({ element }) => elementMatchesFilter(element, filterAttributeName, filterValue, context))
      .map((entry) => entry.value);
   return aggregateValues(aggregator, values, `${functionName}(${relationLabel}.${attributeLabelText}, ${filterAttributeLabelText}, ${formatValue(filterValue, context)})`);
}

function getElementAttributeValue(element: PLAElementRecord, attributeName: string, context: PLAEvaluationContext): { found: boolean; value?: any } {
   if (isElementNameAttribute(attributeName)) return { found: true, value: element.name };
   const attributeId = findAttributeIdByReference(attributeName, context, element);
   const definitions = context.project?.elementTypes[element.elementType]?.attributes || {};
   if (definitions[attributeId]?.type === "expression") {
      if (context.resolveCalculatedAttribute) return { found: true, value: context.resolveCalculatedAttribute(element, attributeId) };
      return { found: true, value: resolvePLACalculatedAttributes(element, context.project!, context.elements, context.relations)[attributeId] };
   }
   if (Object.prototype.hasOwnProperty.call(element.attributes, attributeId)) {
      return { found: true, value: resolveAttributeValue(element.attributes[attributeId], {}, attributeId) };
   }
   return { found: false };
}

export function evaluateParentLookup(relationType: string, attributeName: string, context: PLAEvaluationContext): any {
   if (!context.project) throw new Error("Cannot evaluate parent lookup without project definitions");
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const pending = [context.element.id];
   const visited = new Set<string>([context.element.id]);

   while (pending.length > 0) {
      const currentId = pending.shift()!;
      const parentRelations = getRelatedRelations(context, currentId, relationTypeId, "incoming");
      for (const relation of parentRelations) {
         if (visited.has(relation.fromId)) continue;
         visited.add(relation.fromId);
         const parentElement = getElementsById(context.elements).get(relation.fromId);
         if (!parentElement) throw new Error(`Cannot evaluate parent(${relationLabel}.${attributeLabelText}): missing parent element`);

         try {
            const result = getElementAttributeValue(parentElement, attributeName, context);
            if (result.found) return result.value;
         } catch (error: any) {
            throw new Error(`Cannot evaluate parent(${relationLabel}.${attributeLabelText}) on ${elementLabel(parentElement)}: ${error.message || error}`);
         }
         pending.push(parentElement.id);
      }
   }

   return 0;
}

export function evaluateParentTraversal(aggregator: string, parentRelationType: string, relationType: string, attributeName: string, context: PLAEvaluationContext): any {
   if (!context.project) throw new Error("Cannot evaluate parent traversal without project definitions");
   const parentRelationTypeId = findRelationTypeIdByReference(parentRelationType, context);
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const parentRelationLabel = relationTypeLabel(parentRelationTypeId, context);
   const relationLabel = relationTypeLabel(relationTypeId, context);
   const attributeLabelText = attributeLabel(attributeName, context);
   const pending = [context.element.id];
   const visited = new Set<string>([context.element.id]);

   while (pending.length > 0) {
      const currentId = pending.shift()!;
      const parentRelations = getRelatedRelations(context, currentId, parentRelationTypeId, "incoming");
      for (const parentRelation of parentRelations) {
         if (visited.has(parentRelation.fromId)) continue;
         visited.add(parentRelation.fromId);
         const parentElement = getElementsById(context.elements).get(parentRelation.fromId);
         if (!parentElement) throw new Error(`Cannot evaluate parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText}): missing parent element`);

         const targetRelations = getRelatedRelations(context, parentElement.id, relationTypeId, "outgoing");
         if (targetRelations.length > 0) {
            const values = targetRelations.map((relation) => {
               const targetElement = getElementsById(context.elements).get(relation.toId);
               if (!targetElement) throw new Error(`Cannot evaluate parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText}): missing related element`);
               return getElementAttributeValue(targetElement, attributeName, context).value;
            });
            return aggregateValues(aggregator, values, `parent${aggregator}(${parentRelationLabel}.${relationLabel}.${attributeLabelText})`);
         }

         pending.push(parentElement.id);
      }
   }

   return 0;
}

type CalculatedCache = WeakMap<PLAProject, WeakMap<PLARelationRecord[], Map<PLAElementRecord, Record<string, any>>>>;
const calculatedCache = new WeakMap<PLAElementRecord[], CalculatedCache>();

// Related elements are resolved repeatedly while evaluating traversals; cache per element list, project and relation list.
function resolveRelatedCalculatedAttributes(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): Record<string, any> {
   let byProject = calculatedCache.get(elements);
   if (!byProject) calculatedCache.set(elements, byProject = new WeakMap());
   let byRelations = byProject.get(project);
   if (!byRelations) byProject.set(project, byRelations = new WeakMap());
   let byElement = byRelations.get(relations);
   if (!byElement) byRelations.set(relations, byElement = new Map());
   const cached = byElement.get(element);
   if (cached) return cached;
   const resolved = resolvePLACalculatedAttributes(element, project, elements, relations);
   byElement.set(element, resolved);
   return resolved;
}
export function resolvePLACalculatedAttributes(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], options: { includeErrors?: boolean; errors?: { attributeId: string; message: string }[] } = {}): Record<string, any> {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   const resolved: Record<string, any> = {};
   const resolving = new Set<string>();

   const resolve = (attributeId: string): any => {
      if (Object.prototype.hasOwnProperty.call(resolved, attributeId)) return resolved[attributeId];
      if (resolving.has(attributeId)) throw new Error(`Circular calculated attribute dependency: ${attributeId}`);
      const definition = definitions[attributeId];
      if (!definition || definition.type !== "expression") return resolveAttributeValue(element.attributes[attributeId], resolved, attributeId);
      resolving.add(attributeId);
      try {
         resolved[attributeId] = evaluatePLAExpression(definition.expression || "0", {
            element,
            elements,
            relations,
            project,
            calculated: resolved,
            resolveCalculatedAttribute: (targetElement, targetAttributeId) => {
               if (targetElement.id === element.id) return resolve(targetAttributeId);
               return resolveRelatedCalculatedAttributes(targetElement, project, elements, relations)[targetAttributeId];
            }
         });
      } catch (error: any) {
         options.errors?.push({ attributeId, message: error.message || String(error) });
         if (!options.includeErrors) throw error;
         resolved[attributeId] = error.message || String(error);
      } finally {
         resolving.delete(attributeId);
      }
      return resolved[attributeId];
   };

   Object.keys(definitions).forEach((attributeId) => {
      if (definitions[attributeId].type === "expression") resolve(attributeId);
   });
   return resolved;
}

export function resolvePLAElementExpressionAttributes(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], options: { includeErrors?: boolean } = {}): PLAElementRecord {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   const calculated = resolvePLACalculatedAttributes(element, project, elements, relations, options);
   const attributes = { ...element.attributes };

   Object.entries(definitions).forEach(([attributeId, definition]) => {
      if (definition.type !== "expression") return;
      attributes[attributeId] = { mode: "constant", valueType: definition.valueType || "string", value: calculated[attributeId] };
   });

   return { ...element, attributes };
}

export function validatePLAData(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], options: { ignoreExpressionErrors?: boolean } = {}): string[] {
   const errors: string[] = [];
   const ids = new Set<string>();
   elements.forEach((element) => {
      if (ids.has(element.id)) errors.push(`Duplicate element id: ${element.id}`);
      ids.add(element.id);
      if (!project.elementTypes[element.elementType]) errors.push(`Unknown element type: ${element.elementType}`);
      Object.entries(project.elementTypes[element.elementType]?.attributes || {}).forEach(([attributeId, definition]) => {
         const attribute = element.attributes[attributeId];
         if (definition.valueType !== "color" || attribute?.mode !== "constant") return;
         const values = definition.type === "cube" ? Object.values(attribute.value?.cells || {}) : [attribute.value];
         if (values.some((value) => value !== undefined && value !== null && value !== "" && (typeof value !== "string" || !chroma.valid(value)))) {
            errors.push(`Invalid color: ${element.name || element.id}.${attributeId}`);
         }
      });
      if (options.ignoreExpressionErrors) return;
      const definitions = project.elementTypes[element.elementType]?.attributes || {};
      Object.entries(definitions).forEach(([attributeId, definition]) => {
         if (definition.type === "expression") {
            const validation = validatePLAExpression(definition.expression || "", { project, elementTypeId: element.elementType });
            if (!validation.valid) errors.push(`${attributeId}: ${validation.error}`);
         }
         Object.entries(definition.alerts || {}).forEach(([alertId, alert]) => {
            const validation = validatePLAExpression(alert.booleanExpression, { project, elementTypeId: element.elementType });
            if (!validation.valid) errors.push(`${attributeId}.${alertId}: ${validation.error}`);
         });
      });
      try {
         resolvePLACalculatedAttributes(element, project, elements, relations);
      } catch (error: any) {
         errors.push(error.message || String(error));
      }
   });

   const relationIds = new Set<string>();
   relations.forEach((relation) => {
      if (relationIds.has(relation.id)) errors.push(`Duplicate relation id: ${relation.id}`);
      relationIds.add(relation.id);
      if (!project.relationTypes[relation.relationType]) errors.push(`Unknown relation type: ${relation.relationType}`);
      if (!ids.has(relation.fromId)) errors.push(`Missing from element: ${relation.fromId}`);
      if (!ids.has(relation.toId)) errors.push(`Missing to element: ${relation.toId}`);
   });

   const visiting = new Set<string>();
   const visited = new Set<string>();
   const outgoing = relations.reduce<Record<string, string[]>>((acc, relation) => {
      acc[relation.fromId] = [...(acc[relation.fromId] || []), relation.toId];
      return acc;
   }, {});
   const visit = (id: string): boolean => {
      if (visiting.has(id)) return true;
      if (visited.has(id)) return false;
      visiting.add(id);
      for (const target of outgoing[id] || []) {
         if (visit(target)) return true;
      }
      visiting.delete(id);
      visited.add(id);
      return false;
   };
   Array.from(ids).forEach((id) => {
      if (visit(id)) errors.push(`Circular element relation detected at ${id}`);
   });

   return errors;
}

export function getPLAAlerts(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): string[] {
   return getPLAAlertDetails(element, project, elements, relations).map((alert) => alert.message);
}

export function getPLAAlertDetails(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): { attributeId: string; message: string; optimizerMayOverride?: boolean }[] {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   const alerts: { attributeId: string; message: string; optimizerMayOverride?: boolean }[] = [];
   const calculated = resolvePLACalculatedAttributes(element, project, elements, relations, { includeErrors: true, errors: alerts });
   Object.entries(definitions).forEach(([attributeId, definition]) => {
      Object.values(definition.alerts || {}).forEach((alert) => {
         try {
            if (evaluatePLAExpression(alert.booleanExpression, { element, elements, relations, project, calculated })) {
               alerts.push(alert.optimizerMayOverride ? { attributeId, message: alert.alertMessage, optimizerMayOverride: true } : { attributeId, message: alert.alertMessage });
            }
         } catch (error: any) {
            alerts.push({ attributeId, message: error.message || String(error) });
         }
      });
   });
   return alerts;
}
